<template>
  <!-- ========================== COURSES PAGE ROOT ========================== -->
  <div id="courses">
    <v-container fluid>
      <v-flex xs12>
        <!-- ===== How-To / Helper ===== -->
        <HowToUseThisPage />

        <div class="courses-container">
          <!-- ===== Page Heading ===== -->
          <div class="route-container mb-4 section-heading">
            <h1>
              <Courses_Icon :color="iconColor" class="terms-icon" />
              {{ $t('courses.title') }}
            </h1>
          </div>

          <!-- ===== Top Actions: Create button + Search ===== -->
          <v-flex class="d-flex mb-7 mt-12">
            <!-- Create New Course / Close Builder -->
            <div class="">
              <v-btn
                v-if="
                  !this.openCourseForm && !checkAuthorization(['superAdmin'])
                "
                :color="
                  !alreadyHasCourse && churchid
                    ? 'rgba(165, 216, 103, 1)'
                    : undefined
                "
                :disabled="alreadyHasCourse || !churchid"
                class="elevation-0 font-weight-bold"
                :class="{ 'enable-pointer': alreadyHasCourse }"
                @click="!alreadyHasCourse ? addNewHandler() : ''"
              >
                <!-- Tooltip explaining why disabled if already has a course -->
                <v-tooltip
                  location="bottom"
                  v-if="alreadyHasCourse"
                  max-width="500"
                  content-class="course-message"
                >
                  <template #activator="{ props }">
                    <span v-bind="props">{{
                      $t('courses.createNewCourse')
                    }}</span>
                  </template>
                  <span v-html="courseMsg"></span>
                </v-tooltip>

                <span v-else>{{ $t('courses.createNewCourse') }}</span>
              </v-btn>

              <!-- Close Builder link (visible while form is open) -->
              <span v-else>
                <div
                  v-if="this.openCourseForm"
                  class="close-link"
                  @click="closebuilder"
                >
                  <v-icon :icon="'arrowleft'" />
                  <span class="close-link-text">{{ $t('courses.close') }}</span>
                </div>
              </span>
            </div>

            <v-spacer></v-spacer>

            <!-- Global Search (table) -->
            <v-text-field
              v-if="!openCourseForm"
              v-model="search"
              :append-inner-icon="search ? '' : 'mdi-magnify'"
              :label="$t('courses.searchPlaceholder')"
              single-line
              hide-details
              clearable
              variant="solo"
              flat
              density="compact"
              class="search-input pb-0 pr-3"
              color="#E8E9EC"
              style="max-width: 265px"
            ></v-text-field>
          </v-flex>

          <!-- ========================== COURSE BUILDER (FORM) ========================== -->
          <v-form v-if="openCourseForm" class="mb-12" ref="addeditform">
            <v-card class="py-10 px-15">
              <!-- Builder Title -->
              <v-card-title class="px-0 font-weight-bold font-size-32">
                {{
                  editingCourse
                    ? $t('courses.editCourse')
                    : $t('courses.courseBuilder')
                }}
              </v-card-title>

              <!-- ---------- STEP 1: Title + Start Date ---------- -->
              <v-row>
                <v-col cols="12" class="pb-0">
                  <v-card-text class="text-h5 pa-0">
                    <span class="font-weight-black text-red text-darken-4">{{
                      $t('courses.step1Label')
                    }}</span>
                    <span class="text-subtitle-1 pl-1">{{
                      $t('courses.step1Description')
                    }}</span>
                  </v-card-text>
                </v-col>
              </v-row>

              <v-row>
                <!-- Course Title -->
                <v-col cols="6">
                  <v-card-text
                    class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                  >
                    {{ $t('courses.courseTitle') }}
                  </v-card-text>
                  <v-text-field
                    v-model="courseTitle"
                    variant="outlined"
                    :placeholder="$t('courses.courseTitlePlaceholder')"
                    :rules="[rules.required, rules.length(2)]"
                    :readonly="editingCourse && base"
                    class="user-input mt-1"
                    color="#A0C548"
                    :loading="isediting"
                    style="min-width: 100%"
                  ></v-text-field>
                </v-col>

                <!-- Start Date (superAdmin/leader only, hidden for base courses) -->
                <v-col
                  v-if="
                    !(editingCourse && base) &&
                    isAuthenticated &&
                    checkAuthorization(['superAdmin', 'leader'])
                  "
                  cols="6"
                >
                  <v-card-text
                    class="pa-0 pt-2 mb-1 text-subtitle-1 font-weight-bold text-uppercase"
                  >
                    {{ $t('courses.startDate') }}
                  </v-card-text>

                  <!-- Date Picker -->
                  <v-menu
                    ref="menu"
                    v-model="menu"
                    :close-on-content-click="false"
                    v-model:return-value="startDate"
                    transition="scale-transition"
                    min-width="290px"
                  >
                    <template #activator="{ props }">
                      <v-text-field
                        :model-value="startDate"
                        variant="underlined"
                        class="user-input mt-1"
                        color="#A0C548"
                        append-icon="calendar"
                        readonly
                        v-bind="props"
                        style="min-width: 100%"
                      ></v-text-field>
                    </template>

                    <v-date-picker
                      :model-value="startDate"
                      @update:model-value="onPickDate"
                      no-title
                      scrollable
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        variant="text"
                        color="primary"
                        @click="menu = false"
                        >Cancel</v-btn
                      >
                      <v-btn
                        variant="text"
                        color="primary"
                        @click="$refs.menu.save(startDate)"
                        >OK</v-btn
                      >
                    </v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>

              <!-- Groups selector (STEP 1 — superAdmin only, hidden for base courses) -->
              <v-row
                v-if="
                  !(editingCourse && base) &&
                  isAuthenticated &&
                  checkAuthorization(['superAdmin'])
                "
              >
                <v-col cols="12">
                  <v-card-text
                    class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                  >
                    Groups
                  </v-card-text>

                  <v-card border class="pa-5">
                    <!-- Groups: Filter + Search -->
                    <v-layout wrap class="mb-5">
                      <!-- Alpha Filter -->
                      <div class="letter-filter-container">
                        <span class="font-weight-bold"
                          >{{ $t('courses.filterLabel') }}
                        </span>
                        <v-hover>
                          <template v-slot="{ hover }">
                            <v-icon
                              size="small"
                              class="clear-filter"
                              :color="`${hover ? 'black' : ''}`"
                              @click="filterlist('', '')"
                              :icon="'mdi-filter-off'"
                            />
                          </template>
                        </v-hover>

                        <ul class="mb-0 pl-0">
                          <li v-for="(letter, i) in filters" :key="i">
                            <span
                              class="filter-letter"
                              :class="{ active: activeindex === i }"
                              @click="filterlist(letter, i)"
                              >{{ letter }}</span
                            >
                          </li>
                        </ul>
                      </div>

                      <v-spacer></v-spacer>

                      <!-- Search Groups -->
                      <div class="search-container">
                        <v-text-field
                          v-model="groupSearch"
                          color="#E8E9EC"
                          class="search-field"
                          variant="outlined"
                          flat
                          density="compact"
                          :label="$t('courses.searchPlaceholder')"
                          :append-inner-icon="groupSearch ? '' : 'mdi-magnify'"
                          single-line
                          clearable
                          hide-details
                          @keydown="resetPagination"
                        ></v-text-field>
                      </div>
                    </v-layout>

                    <!-- Groups: Paginated list -->
                    <div class="groups-selection-scroll">
                      <v-row class="groups-selection-row">
                        <v-col
                          v-for="group in displayGroups"
                          :key="group.group_doc_id"
                          cols="12"
                          sm="6"
                          md="4"
                          class="pa-0"
                        >
                          <v-checkbox
                            class="mt-0 pa-0"
                            false-icon="mdi-radiobox-blank"
                            true-icon="mdi-radiobox-marked"
                            :id="group.group_doc_id"
                            v-model="selectedGroup"
                            :label="group.name"
                            :color="isDarkTheme ? '#efefef' : '#000000'"
                            :disabled="group.disabled || group.tempdisabled"
                            :value="group"
                            @update:model-value="groupSelected(group)"
                          />
                        </v-col>
                      </v-row>
                    </div>

                    <!-- Groups: Pagination -->
                    <v-pagination
                      v-model="currPage"
                      :length="Math.ceil(filteredGroups.length / pageSize)"
                      :total-visible="
                        Math.ceil(filteredGroups.length / pageSize)
                      "
                      rounded
                      density="compact"
                      active-color="#a5d867"
                      :color="isDarkTheme ? '#efefef' : '#000000'"
                    />
                  </v-card>
                </v-col>
              </v-row>

              <!-- Language Selection (STEP 1, hidden for base courses) -->
              <v-row v-if="!(editingCourse && base)">
                <v-col cols="12">
                  <v-card-text
                    class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                  >
                    {{ $t('courses.requiredLanguage') }}
                  </v-card-text>
                  <v-alert
                    v-if="!requiredLanguage"
                    type="warning"
                    density="compact"
                    variant="outlined"
                    class="mt-2"
                  >
                    {{ $t('courses.pleaseSelectLanguage') }}
                  </v-alert>

                  <v-alert
                    v-else-if="availableSections.length === 0"
                    type="warning"
                    density="compact"
                    variant="outlined"
                    class="mt-2"
                  >
                    <div
                      v-html="
                        $t('courses.noSectionsAvailable', {
                          language:
                            availableLanguages.find(
                              (l) => l.code === requiredLanguage
                            )?.label || requiredLanguage.toUpperCase(),
                        })
                      "
                    ></div>
                  </v-alert>

                  <v-alert
                    v-else
                    type="info"
                    density="compact"
                    variant="outlined"
                    class="mt-2"
                  >
                    <div
                      v-html="
                        $t('courses.languageInfo', {
                          language:
                            availableLanguages.find(
                              (l) => l.code === requiredLanguage
                            )?.label || requiredLanguage.toUpperCase(),
                        })
                      "
                    ></div>
                  </v-alert>

                  <v-radio-group v-model="requiredLanguage" inline class="mt-2">
                    <v-radio
                      v-for="lang in availableLanguages"
                      :key="lang.code"
                      :label="lang.label"
                      :value="lang.code"
                    ></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>

              <!-- Base template language filter (STEP 1, base-course edit only) -->
              <v-row v-else>
                <v-col cols="12">
                  <v-card-text
                    class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                  >
                    {{ $t('courses.filterModulesByLanguage') }}
                  </v-card-text>
                  <v-alert
                    v-if="!filterLanguage"
                    type="warning"
                    density="compact"
                    variant="outlined"
                    class="mt-2"
                  >
                    Select a language to show matching modules in Customize Your
                    Course.
                  </v-alert>

                  <v-alert
                    v-else
                    type="info"
                    density="compact"
                    variant="outlined"
                    class="mt-2"
                  >
                    Showing
                    {{
                      availableLanguages.find((l) => l.code === filterLanguage)
                        ?.label || filterLanguage.toUpperCase()
                    }}
                    modules in Customize Your Course. Existing modules from
                    other languages remain part of this shared base template
                    unless you remove them.
                  </v-alert>

                  <v-radio-group
                    :model-value="filterLanguage"
                    inline
                    class="mt-2"
                    @update:model-value="setBaseTemplateLanguageFilter"
                  >
                    <v-radio
                      v-for="lang in availableLanguages"
                      :key="lang.code"
                      :label="lang.label"
                      :value="lang.code"
                    ></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>

              <!-- ---------- STEP 2: Choose a base course ---------- -->
              <v-card-text class="text-h5 pa-0 pt-6">
                <span class="font-weight-black text-red text-darken-4">{{
                  $t('courses.step2Label')
                }}</span>
                <span class="text-subtitle-1 pl-1">
                  {{ $t('courses.step2Description') }}
                </span>
              </v-card-text>

              <!-- Choose a Course subtitle -->
              <v-card-text class="pa-0 pt-6 text-h5">
                <span class="font-weight-bold text-uppercase">{{
                  $t('courses.chooseACourse')
                }}</span>
                <span class="text-subtitle-2 ml-3"
                  >({{ $t('courses.customizeDescription') }})</span
                >
              </v-card-text>

              <v-divider class="mt-4 mb-5"></v-divider>

              <!-- Default course presets component -->
              <DefaultCourses
                v-if="
                  isAuthenticated &&
                  checkAuthorization(['superAdmin', 'admin', 'leader'])
                "
                :sections="sections"
                :courses="courses"
                :courseToEdit="courseToEdit"
                :requiredLanguage="requiredLanguage"
                @listevent="handlerDefaultCourses"
              />

              <!-- ---------- STEP 3: Customize (drag & drop) ---------- -->
              <span v-if="selectedSections.length > 0 || newBase">
                <v-divider class="mt-4 mb-5"></v-divider>

                <v-card
                  class="pa-5"
                  elevation="0"
                  :color="isDarkTheme ? 'grey-darken-3' : 'grey-lighten-5'"
                >
                  <!-- Step 3 header -->
                  <v-card-text class="text-h5 pa-0">
                    <span class="font-weight-black text-red text-darken-4">{{
                      $t('courses.step3Label')
                    }}</span>
                    <span class="text-subtitle-1 pl-1">
                      {{ $t('courses.step3Description') }}
                    </span>
                  </v-card-text>

                  <!-- Step 3 subheading -->
                  <v-layout wrap class="mt-3">
                    <v-col cols="12" sm="6" class="text-subtitle-2 pa-0">
                      <div class="text-h5 font-weight-bold text-uppercase">
                        {{ $t('courses.customizeYourCourse') }}
                      </div>
                      <div class="text-subtitle-2 mt-4 mr-8">
                        {{ $t('courses.customizeInstructions') }}
                      </div>
                    </v-col>
                  </v-layout>

                  <v-divider class="mt-4 mb-5"></v-divider>

                  <!-- ===== Drag & Drop Builder ===== -->
                  <v-layout wrap class="mt-12 select-sections">
                    <!-- Filters column -->
                    <v-col cols="12" sm="3" class="pa-0 pr-12 filters-column">
                      <div
                        class="text-subtitle-1 font-weight-bold text-uppercase"
                      >
                        {{ $t('courses.filters') }}
                      </div>
                      <v-divider class="mt-4 mb-5"></v-divider>

                      <!-- Filters: Language / Topics / Authors / Section Length -->
                      <v-expansion-panels
                        v-model="openedPanel"
                        multiple
                        elevation="0"
                        class="pa-0 filter-expansion-panels"
                      >
                        <!-- Language filter -->
                        <v-expansion-panel>
                          <v-expansion-panel-title class="px-0 pt-0 pb-0">
                            <div class="font-weight-bold builder-title">
                              {{ $t('courses.filterLanguage') }}
                            </div>
                            <template #actions="{ expanded }">
                              <v-icon
                                class="transition-transform"
                                :class="{ 'rotate-180': expanded }"
                              >
                                arrowdown
                              </v-icon>
                            </template>
                          </v-expansion-panel-title>
                          <v-expansion-panel-text class="pa-0">
                            <div
                              class="clear-filter-topics"
                              @click="
                                ;(filterLanguage = ''),
                                  (activeLanguageIndex = undefined)
                              "
                            >
                              <span
                                v-if="filterLanguage"
                                class="text-caption text-red font-weight-bold"
                              >
                                <v-icon color="red" :icon="'mdi-undo'" /> [
                                {{ $t('courses.resetLanguage') }}]
                              </span>
                            </div>
                            <ul class="topic-filter pt-0 pl-0">
                              <li
                                v-for="(lang, index) in availableLanguages"
                                :key="index"
                                @click="
                                  ;(filterLanguage = lang.code),
                                    (activeLanguageIndex = index)
                                "
                                :class="{
                                  'font-weight-bold':
                                    activeLanguageIndex === index,
                                }"
                              >
                                {{ lang.label }}
                              </li>
                            </ul>
                          </v-expansion-panel-text>
                        </v-expansion-panel>

                        <!-- Topics filter -->
                        <v-expansion-panel>
                          <v-expansion-panel-title class="px-0 pt-0 pb-0">
                            <div class="font-weight-bold builder-title">
                              {{ $t('courses.topics') }}
                            </div>
                            <template #actions="{ expanded }">
                              <v-icon
                                class="transition-transform"
                                :class="{ 'rotate-180': expanded }"
                              >
                                arrowdown
                              </v-icon>
                            </template>
                          </v-expansion-panel-title>
                          <v-expansion-panel-text class="pa-0">
                            <div
                              class="clear-filter-topics"
                              @click="
                                ;(topics = ''), (activeTopicIndex = undefined)
                              "
                            >
                              <span
                                v-if="topics"
                                class="text-caption text-red font-weight-bold"
                              >
                                <v-icon color="red" :icon="'mdi-undo'" /> [{{
                                  $t('courses.resetTopics')
                                }}]
                              </span>
                            </div>
                            <ul class="topic-filter pt-0 pl-0">
                              <li
                                v-for="(n, index) in allSectionCategories"
                                :key="index"
                                @click="
                                  ;(topics = n.id), (activeTopicIndex = index)
                                "
                                :class="{
                                  'font-weight-bold':
                                    activeTopicIndex === index,
                                }"
                              >
                                {{ n.name }}
                              </li>
                            </ul>
                          </v-expansion-panel-text>
                        </v-expansion-panel>

                        <!-- Authors filter -->
                        <v-expansion-panel class="mt-0">
                          <v-expansion-panel-title class="px-0 pt-4 pb-0">
                            <div class="font-weight-bold builder-title">
                              {{ $t('courses.authors') }}
                            </div>
                            <template #actions="{ expanded }">
                              <v-icon
                                class="transition-transform"
                                :class="{ 'rotate-180': expanded }"
                              >
                                arrowdown
                              </v-icon>
                            </template>
                          </v-expansion-panel-title>
                          <v-expansion-panel-text class="pa-0">
                            <div
                              class="clear-filter-topics"
                              @click="
                                ;(authors = ''), (activeAuthorIndex = undefined)
                              "
                            >
                              <span
                                v-if="authors"
                                class="text-caption text-red font-weight-bold"
                              >
                                <v-icon color="red" :icon="'mdi-undo'" /> [{{
                                  $t('courses.resetAuthors')
                                }}]
                              </span>
                            </div>
                            <ul class="author-filter pt-0 pl-0">
                              <li
                                v-for="(a, index) in sectionAuthors"
                                :key="index"
                                @click="
                                  ;(authors = a), (activeAuthorIndex = index)
                                "
                                :class="{
                                  'font-weight-bold':
                                    activeAuthorIndex === index,
                                }"
                              >
                                {{ a }}
                              </li>
                            </ul>
                          </v-expansion-panel-text>
                        </v-expansion-panel>

                        <!-- Weeks filter -->
                        <v-expansion-panel class="mt-0">
                          <v-expansion-panel-title class="px-0 pt-4 pb-0">
                            <div class="font-weight-bold builder-title">
                              {{ $t('courses.sectionLength') }}
                            </div>
                            <template #actions="{ expanded }">
                              <v-icon
                                class="transition-transform"
                                :class="{ 'rotate-180': expanded }"
                              >
                                arrowdown
                              </v-icon>
                            </template>
                          </v-expansion-panel-title>
                          <v-expansion-panel-text class="pa-0">
                            <div
                              class="clear-filter-weeks"
                              @click="numOfWeeks = ''"
                            >
                              <span
                                v-if="numOfWeeks"
                                class="text-caption text-red font-weight-bold"
                              >
                                <v-icon color="red" :icon="'mdi-undo'" /> [{{
                                  $t('courses.resetWeeks')
                                }}]
                              </span>
                            </div>
                            <v-radio-group
                              v-model="numOfWeeks"
                              class="pt-0 ma-0"
                            >
                              <v-radio
                                v-for="n in uniqueWeeks"
                                :key="n"
                                :label="`${n}` + (n == 1 ? ' Week' : ' Weeks')"
                                :value="n"
                                hide-details
                              ></v-radio>
                            </v-radio-group>
                          </v-expansion-panel-text>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>

                    <!-- Available Sections (left) -->
                    <v-col cols="12" sm="4" class="pa-0 available-sections-col">
                      <div
                        class="text-subtitle-1 font-weight-bold text-uppercase"
                      >
                        {{ $t('courses.availableSections') }}
                      </div>

                      <!-- Sort controls -->
                      <div class="text-caption text-uppercase">
                        <v-btn
                          size="small"
                          variant="text"
                          min-width="inherit"
                          height="inherit"
                          disabled
                          tile
                          class="pl-0 pr-1"
                          >{{ $t('courses.sort') }}</v-btn
                        >
                        <v-btn
                          size="small"
                          variant="text"
                          min-width="inherit"
                          height="inherit"
                          tile
                          class="font-weight-bold pa-0 sort-btn"
                          @click="sortSections('ASC')"
                          >A-Z</v-btn
                        >
                        <v-btn
                          size="small"
                          variant="text"
                          min-width="inherit"
                          height="inherit"
                          tile
                          class="font-weight-bold pa-0 ml-3 sort-btn"
                          @click="sortSections('DESC')"
                          >Z-A</v-btn
                        >
                      </div>

                      <!-- Draggable list (left) -->
                      <template v-if="readyToRenderSections">
                        <draggable
                          class="list-group mt-2"
                          tag="ul"
                          :list="list"
                          v-bind="dragOptions"
                          @start="isDragging = true"
                          @end="isDragging = false"
                          @change="listChanged"
                          :emptyInsertThreshold="500"
                          handle=".handle"
                          item-key="id"
                        >
                          <template #item="{ element, index }">
                            <v-list
                              v-show="matchesAvailableFilters(element)"
                              rounded
                              border
                              :key="element.id"
                              class="border-grey mb-5 py-0 px-2 all-sections"
                              :class="index"
                            >
                              <div class="flex-column pb-3 d-flex">
                                <!-- Title / drag handle -->
                                <div>
                                  <v-list-item
                                    class="handle section-title pr-2 pl-0"
                                  >
                                    <v-list-item-title
                                      class="text-uppercase _caption font-weight-bold"
                                    >
                                      {{ element.title }}
                                    </v-list-item-title>
                                  </v-list-item>
                                </div>

                                <!-- Meta row: category • year dots • weeks • info -->
                                <div class="d-flex align-center">
                                  <div
                                    class="available-section-category text-caption"
                                  >
                                    {{ featuredSectionCategory(element) }}
                                  </div>

                                  <!-- Base year indicators -->
                                  <ul
                                    class="baseyear-indicators-container ml-auto mb-0 pa-0"
                                  >
                                    <span
                                      v-for="theItem in checkForDefaultYear(
                                        element
                                      )"
                                      :key="theItem.id"
                                    >
                                      <v-tooltip location="bottom">
                                        <template #activator="{ props }">
                                          <li
                                            v-bind="props"
                                            :class="theItem"
                                          ></li>
                                        </template>
                                        <span
                                          >{{ yearLabel(theItem) }} base
                                          section</span
                                        >
                                      </v-tooltip>
                                    </span>
                                  </ul>

                                  <!-- Weeks badge -->
                                  <div
                                    class="section-units text-right pa-0 mr-3 align-self-baseline"
                                  >
                                    <span class="badge text-caption"
                                      >{{ element.units.length }}
                                      {{ $t('courses.weeksAbbr') }}</span
                                    >
                                  </div>

                                  <!-- Info icon -->
                                  <div class="featured-sections-card-footer">
                                    <v-icon
                                      class="information-icon"
                                      @click.native="
                                        previewSectionInfo(element)
                                      "
                                      :icon="'mdi-information'"
                                    />
                                  </div>
                                </div>
                              </div>
                            </v-list>
                          </template>
                        </draggable>
                      </template>
                      <template v-else>
                        <!-- optional: loading indicator -->
                        <div class="text-caption mt-4">
                          {{ $t('courses.loadingSections') }}
                        </div>
                      </template>
                      <template
                        v-if="readyToRenderSections && list.length === 0"
                      >
                        <v-alert
                          type="info"
                          density="compact"
                          variant="outlined"
                          class="mt-4"
                        >
                          <div
                            v-html="
                              requiredLanguage
                                ? $t('courses.noSectionsAvailable', {
                                    language:
                                      availableLanguages.find(
                                        (l) => l.code === requiredLanguage
                                      )?.label ||
                                      requiredLanguage.toUpperCase(),
                                  })
                                : $t('courses.noSectionsFound')
                            "
                          ></div>
                        </v-alert>
                      </template>
                    </v-col>

                    <!-- Transfer arrow / divider -->
                    <v-col class="d-flex align-center justify-center">
                      <v-icon
                        :color="this.isDarkTheme ? '#CECECE' : '#383935'"
                        size="30"
                        :icon="'mdi-autorenew'"
                      />
                    </v-col>

                    <!-- Your Sections (right) -->
                    <v-col cols="12" sm="4" class="pa-0">
                      <div
                        class="text-subtitle-1 font-weight-bold text-uppercase"
                      >
                        {{ $t('courses.yourSections') }}
                      </div>
                      <div
                        v-if="editingCourse && base && filterLanguage"
                        class="text-caption mt-1"
                      >
                        Showing only
                        {{
                          availableLanguages.find((l) => l.code === filterLanguage)
                            ?.label || filterLanguage.toUpperCase()
                        }}
                        modules from this shared base template.
                      </div>
                      <div
                        v-if="weekCountsByLang.length > 0"
                        class="text-caption mt-1"
                      >
                        <span
                          v-for="(count, index) in weekCountsByLang"
                          :key="index"
                        >
                          <span v-if="index > 0">, </span>
                          {{ count.lang.toUpperCase() }}: {{ count.weeks }}
                          {{
                            count.weeks === 1
                              ? $t('courses.week')
                              : $t('courses.weeks')
                          }}
                        </span>
                      </div>

                      <!-- Draggable list (right) -->
                      <draggable
                        element="span"
                        class="list-group mt-2 selected-sections-list"
                        tag="ul"
                        v-model="selectedSections"
                        v-bind="dragOptions"
                        :emptyInsertThreshold="500"
                        handle=".handle"
                        item-key="id"
                      >
                        <template #item="{ element, index }">
                          <v-list
                            v-show="matchesSelectedLanguageFilter(element)"
                            rounded
                            border
                            :key="element.id"
                            class="border-grey mb-5 py-0 px-2 selected"
                            :class="{
                              fixed: element.locked || element.superLocked,
                            }"
                          >
                            <div
                              class="selected-sections flex-column pb-3 d-flex"
                            >
                              <!-- Title + lock toggle -->
                              <div
                                class="d-flex justify-space-between align-center"
                              >
                                <v-list-item
                                  :class="{
                                    handle:
                                      !element.locked && !element.superLocked,
                                  }"
                                  class="section-title pr-2 pl-0 flex-1"
                                >
                                  <v-list-item-title
                                    class="text-uppercase _caption font-weight-bold"
                                  >
                                    {{ element.title }}
                                  </v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                  class="ml-auto lock-item px-2"
                                  @click="lockSection(element, index)"
                                >
                                  <v-icon
                                    :icon="
                                      element.locked || element.superLocked
                                        ? 'lock'
                                        : 'lock_open'
                                    "
                                  />
                                </v-list-item>
                              </div>

                              <!-- Meta row: category • year dots • weeks • info -->
                              <div class="d-flex align-center">
                                <div
                                  class="available-section-category text-caption"
                                >
                                  {{ featuredSectionCategory(element) }}
                                </div>

                                <!-- Base year indicators -->
                                <ul
                                  class="baseyear-indicators-container ml-auto mb-0 pa-0"
                                >
                                  <span
                                    v-for="theItem in checkForDefaultYear(
                                      element
                                    )"
                                    :key="theItem.id"
                                  >
                                    <v-tooltip location="bottom">
                                      <template #activator="{ props }">
                                        <li
                                          v-bind="props"
                                          :class="theItem"
                                        ></li>
                                      </template>
                                      <span
                                        >{{ yearLabel(theItem) }} base
                                        section</span
                                      >
                                    </v-tooltip>
                                  </span>
                                </ul>

                                <!-- Weeks badge -->
                                <div
                                  class="section-units text-right pa-0 mr-3 align-self-baseline"
                                >
                                  <span class="badge text-caption"
                                    >{{ element.units.length }}
                                    {{ $t('courses.weeksAbbr') }}</span
                                  >
                                </div>

                                <!-- Info icon -->
                                <div class="featured-sections-card-footer">
                                  <v-icon
                                    class="information-icon"
                                    @click.native="previewSectionInfo(element)"
                                    :icon="'mdi-information'"
                                  />
                                </div>
                              </div>
                            </div>
                          </v-list>
                        </template>
                      </draggable>
                      <v-alert
                        v-if="
                          editingCourse &&
                          base &&
                          filterLanguage &&
                          visibleSelectedSectionsCount === 0
                        "
                        type="info"
                        density="compact"
                        variant="outlined"
                        class="mt-4"
                      >
                        No
                        {{
                          availableLanguages.find((l) => l.code === filterLanguage)
                            ?.label || filterLanguage.toUpperCase()
                        }}
                        modules are currently in this shared base template.
                      </v-alert>
                    </v-col>
                  </v-layout>
                </v-card>
              </span>
              <!-- ---------- / STEP 3 ---------- -->
            </v-card>

            <!-- ===== Submit / Save button with tooltip guard ===== -->
            <div class="text-center">
              <v-tooltip
                location="bottom"
                :disabled="hasValidMissionCoverage"
              >
                <template #activator="{ props }">
                  <span v-bind="props">
                    <v-btn
                      :loading="isSubmitting"
                      :disabled="
                        isSubmitting ||
                        selectedSections.length == 0 ||
                        !hasValidMissionCoverage
                      "
                      color="rgba(165, 216, 103, 1)"
                      class="elevation-0 font-weight-bold my-14 px-14"
                      @click="editingCourse ? editCourse() : addCourse()"
                    >
                      {{ authorizedActions }}
                    </v-btn>
                  </span>
                </template>
                <div class="text-center">
                  <template v-if="isSharedBaseTemplateEdit">
                    <p
                      v-for="requirement in missingMissionRequirements"
                      :key="`${requirement.lang}-${requirement.weeks}`"
                      class="my-0"
                    >
                      No mission is available for
                      {{ requirement.lang.toUpperCase() }} with
                      {{ requirement.weeks }} weeks.
                    </p>
                    <p
                      v-for="requirement in missingMissionRequirements"
                      :key="`${requirement.lang}-${requirement.weeks}-available`"
                      class="my-0"
                    >
                      Available {{ requirement.lang.toUpperCase() }} lengths:
                      {{
                        requirement.available.length
                          ? requirement.available.join(', ')
                          : 'none'
                      }}
                      weeks.
                    </p>
                    <p class="my-0">
                      Adjust the modules in this shared template until each
                      language has a matching mission length.
                    </p>
                  </template>
                  <template v-else>
                    <p class="my-0">
                      No mission is available for
                      {{ (requiredLanguage || 'en').toUpperCase() }} with
                      {{ totalWeeks }} weeks.
                    </p>
                    <p class="my-0" v-if="availableMissionWeekCounts.length">
                      Available lengths:
                      {{ availableMissionWeekCounts.join(', ') }} weeks.
                    </p>
                    <p class="my-0">
                      Adjust the number of weeks or change the language.
                    </p>
                  </template>
                </div>
              </v-tooltip>
            </div>

            <!-- Builder progress + divider -->
            <v-progress-linear
              :indeterminate="iseditingCourse"
              :active="iseditingCourse"
              color="#592892"
            ></v-progress-linear>
            <v-divider class="dashed"></v-divider>
          </v-form>
          <!-- ========================== / COURSE BUILDER ========================== -->

          <!-- ===== Inline Feedback Alert ===== -->
          <v-alert
            v-if="feedback"
            border="start"
            color="#592892"
            class="text-center"
          >
            {{ feedback }}
          </v-alert>

          <!-- ===== Table Filters (Archived toggle) ===== -->
          <v-flex class="text-caption text-uppercase d-flex mb-3">
            <v-btn
              size="small"
              variant="text"
              min-width="inherit"
              height="inherit"
              disabled
              tile
              class="pl-0 pr-1 filter-btn"
            >
              {{ $t('courses.filterLabel') }}
            </v-btn>
            <v-btn
              size="small"
              variant="text"
              min-width="inherit"
              height="inherit"
              tile
              class="font-weight-bold pa-0 sort-btn"
              @click="courseFilters('show-archived')"
            >
              {{
                areArchivedCoursesVisible
                  ? $t('courses.filter.hide')
                  : $t('courses.filter.show')
              }}
              {{ $t('courses.filter.archived') }}
            </v-btn>
          </v-flex>

          <!-- ========================== COURSES TABLE WITH TABS ========================== -->
          <!-- Tabs only visible to superAdmin -->
          <template v-if="checkAuthorization(['superAdmin'])">
            <v-tabs v-model="currentTab" class="mb-4">
              <v-tab value="regular">Regular Courses</v-tab>
              <v-tab value="base">Base Courses</v-tab>
            </v-tabs>

            <v-window v-model="currentTab">
              <!-- Regular Courses Tab -->
              <v-window-item value="regular">
                <v-data-table
                  :headers="regularCoursesHeaders"
                  fixed-header
                  :loading="isloading"
                  :loading-text="$t('courses.loadingCourses')"
                  :no-data-text="$t('courses.noCoursesFound')"
                  :items="visibleCourses"
                  :items-per-page="20"
                  :header-props="{ sortIcon: '$sortarrowdown' }"
                  :footer-props="{
                    'items-per-page-options': [10, 20, 30, 40, 50, -1],
                  }"
                  item-key="id"
                  :row-props="(item) => ({ class: row_classes(item) })"
                  :search="search"
                  class="elevation-1 users-table mb-16"
                >
                  <!-- Table: loading state -->
                  <template #progress>
                    <v-progress-linear
                      color="#592892"
                      indeterminate
                    ></v-progress-linear>
                  </template>

                  <!-- Column: Title + Base Year label -->
                  <template #[`item.title`]="{ item }">
                    <div v-if="item.title.length" class="text-capitalize">
                      <span class="year-title" :class="item.baseYear">{{
                        item.title
                      }}</span>
                    </div>
                    <div
                      v-if="item.baseYear.length && !item.base"
                      class="text-caption"
                    >
                      <span class="course-base-year" :class="item.baseYear">
                        <span v-if="item.base">BASE: </span
                        >{{ interpret(item.baseYear) }}
                      </span>
                    </div>
                  </template>

                  <!-- Column: Sections (popover listing) -->
                  <template #[`item.sections`]="{ item }">
                    <div v-if="item.sections.length">
                      <div class="text-center">
                        <v-menu location="bottom">
                          <template #activator="{ props }">
                            <v-btn variant="text" v-bind="props">
                              <v-icon
                                start
                                class="text-on-surface"
                                :icon="'mdi-eye'"
                              />
                              {{ item.sections.length }}
                            </v-btn>
                          </template>
                          <v-list>
                            <v-list-item
                              v-for="(sec, i) in item.sections"
                              :key="i"
                            >
                              <v-list-item-title>{{
                                lookUpSection(sec)
                              }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </div>
                  </template>

                  <!-- Column: Units count -->
                  <template #[`item.units`]="{ item }">
                    <div v-if="item.units.length">{{ item.units.length }}</div>
                  </template>

                  <!-- Column: Language -->
                  <template #[`item.requiredLanguage`]="{ item }">
                    <!-- Regular courses tab: show single required language -->
                    <v-chip
                      size="small"
                      :color="getLanguageColor(item.requiredLanguage || 'en')"
                      variant="outlined"
                    >
                      {{ (item.requiredLanguage || 'en').toUpperCase() }}
                    </v-chip>
                  </template>

                  <!-- Column: Start Date -->
                  <template #[`item.startDate`]="{ item }">
                    <div v-if="item.startDate">{{ item.startDate }}</div>
                  </template>

                  <!-- Column: Status chip (click-to-verify for admins) -->
                  <template #[`item.status`]="{ item }">
                    <v-tooltip
                      :disabled="!checkIfLeader(item)"
                      location="top"
                      content-class="text-center"
                      max-width="500"
                    >
                      <template #activator="{ props }">
                        <v-chip
                          v-bind="props"
                          :class="[
                            item.status == 'verified'
                              ? isDarkTheme
                                ? 'text-grey-lighten-3'
                                : 'text-black'
                              : isDarkTheme
                              ? 'text-black'
                              : 'text-grey-lighten-1',
                          ]"
                          :color="
                            item.status == 'verified'
                              ? 'rgba(165, 216, 103, .32)'
                              : ''
                          "
                          variant="flat"
                          @click.native="handleClick(item)"
                        >
                          <span v-if="item.base">Base Year</span>
                          <span v-else>{{ interpret(item.status) }}</span>
                        </v-chip>
                      </template>
                      <span v-if="item.status == 'verified'">
                        To view the course, click on the 'N' in the top left to
                        return to the home screen. Then, open the Courses drawer
                        in the top left.
                      </span>
                      <span v-else>
                        You will receive an email when your admin has approved
                        your course. Come back here and refresh the page.
                      </span>
                    </v-tooltip>
                  </template>

                  <!-- Column: Group Name -->
                  <template #[`item.selectedGroupName`]="{ item }">
                    <div v-if="item.selectedGroupName">
                      {{ item.selectedGroupName }}
                    </div>
                  </template>

                  <!-- Column: Row Actions (Edit / Archive / Delete) -->
                  <template
                    v-if="
                      isAuthenticated &&
                      checkAuthorization(['superAdmin', 'admin', 'leader'])
                    "
                    #[`item.actions`]="{ item }"
                  >
                    <div
                      v-if="!item.archive || checkAuthorization(['superAdmin'])"
                      class="action-icons"
                    >
                      <!-- Edit -->
                      <v-icon
                        v-if="
                          !isHelpDesk() &&
                          (checkAuthorization(['superAdmin']) ||
                            (item.status == 'unverified' && checkIfLeader(item)))
                        "
                        @click.native="loadCourseToEdit(item)"
                        class="mr-6"
                        :icon="'edit'"
                      />

                      <!-- Delete (only superAdmin on archived) -->
                      <span
                        v-if="
                          item.archive &&
                          checkAuthorization(['superAdmin']) &&
                          !isHelpDesk()
                        "
                        class="action-icons"
                      >
                        <v-tooltip location="bottom">
                          <template #activator="{ props }">
                            <span v-bind="props">
                              <v-icon
                                @click.native="deleteCourse(item)"
                                class="delete-icon"
                                :icon="'delete_icon'"
                              />
                            </span>
                          </template>
                          <span>{{ $t('courses.deleteCourse') }}</span>
                        </v-tooltip>
                      </span>

                      <!-- Archive -->
                      <span v-else>
                        <v-tooltip
                          v-if="
                            checkIfLeader(item) ||
                            checkAuthorization(['superAdmin', 'admin'])
                          "
                          location="bottom"
                        >
                          <template #activator="{ props }">
                            <span v-bind="props">
                              <v-icon
                                v-if="!item.base"
                                @click.native="archiveCourse(item)"
                                class="archive-icon"
                                :icon="'logout'"
                              />
                              <span v-else class="px-2"></span>
                            </span>
                          </template>
                          <span>{{ $t('courses.archiveCourse') }}</span>
                        </v-tooltip>
                      </span>
                    </div>
                  </template>
                </v-data-table>
              </v-window-item>

              <!-- Base Courses Tab -->
              <v-window-item value="base">
                <v-data-table
                  :headers="baseCoursesHeaders"
                  fixed-header
                  :loading="isloading"
                  :loading-text="$t('courses.loadingCourses')"
                  :no-data-text="$t('courses.noCoursesFound')"
                  :items="visibleCourses"
                  :items-per-page="20"
                  :header-props="{ sortIcon: '$sortarrowdown' }"
                  :footer-props="{
                    'items-per-page-options': [10, 20, 30, 40, 50, -1],
                  }"
                  item-key="id"
                  :row-props="(item) => ({ class: row_classes(item) })"
                  :search="search"
                  class="elevation-1 users-table mb-16"
                >
                  <!-- Table: loading state -->
                  <template #progress>
                    <v-progress-linear
                      color="#592892"
                      indeterminate
                    ></v-progress-linear>
                  </template>

                  <!-- Column: Title + Base Year label -->
                  <template #[`item.title`]="{ item }">
                    <div v-if="item.title.length" class="text-capitalize">
                      <span class="year-title" :class="item.baseYear">{{
                        item.title
                      }}</span>
                    </div>
                  </template>

                  <!-- Column: Sections (popover listing) -->
                  <template #[`item.sections`]="{ item }">
                    <div v-if="item.sections.length">
                      <div class="text-center">
                        <v-menu location="bottom">
                          <template #activator="{ props }">
                            <v-btn variant="text" v-bind="props">
                              <v-icon
                                start
                                class="text-on-surface"
                                :icon="'mdi-eye'"
                              />
                              {{ item.sections.length }}
                            </v-btn>
                          </template>
                          <v-list>
                            <v-list-item
                              v-for="(sec, i) in item.sections"
                              :key="i"
                            >
                              <v-list-item-title>{{
                                lookUpSection(sec)
                              }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </div>
                  </template>

                  <!-- Column: Units count -->
                  <template #[`item.units`]="{ item }">
                    <div v-if="item.units.length">{{ item.units.length }}</div>
                  </template>

                  <!-- Column: Language -->
                  <template #[`item.requiredLanguage`]="{ item }">
                    <v-chip
                      v-for="lang in getCourseLanguages(item)"
                      :key="lang"
                      size="small"
                      :color="getLanguageColor(lang)"
                      variant="outlined"
                      class="mr-1"
                    >
                      {{ lang.toUpperCase() }}
                    </v-chip>
                  </template>

                  <!-- Column: Row Actions (Edit / Archive / Delete) -->
                  <template
                    v-if="
                      isAuthenticated &&
                      checkAuthorization(['superAdmin', 'admin', 'leader'])
                    "
                    #[`item.actions`]="{ item }"
                  >
                    <div
                      v-if="!item.archive || checkAuthorization(['superAdmin'])"
                      class="action-icons"
                    >
                      <!-- Edit -->
                      <v-icon
                        v-if="checkAuthorization(['superAdmin'])"
                        @click.native="loadCourseToEdit(item)"
                        class="mr-6"
                        :icon="'edit'"
                      />
                    </div>
                  </template>
                </v-data-table>
              </v-window-item>
            </v-window>
          </template>

          <!-- Non-superAdmin: Single table without tabs -->
          <template v-else>
            <v-data-table
              :headers="regularCoursesHeaders"
              fixed-header
              :loading="isloading"
              :loading-text="$t('courses.loadingCourses')"
              :no-data-text="$t('courses.noCoursesFound')"
              :items="visibleCourses"
              :items-per-page="20"
              :header-props="{ sortIcon: '$sortarrowdown' }"
              :footer-props="{
                'items-per-page-options': [10, 20, 30, 40, 50, -1],
              }"
              item-key="id"
              :row-props="(item) => ({ class: row_classes(item) })"
              :search="search"
              class="elevation-1 users-table mb-16"
            >
              <!-- Table: loading state -->
              <template #progress>
                <v-progress-linear
                  color="#592892"
                  indeterminate
                ></v-progress-linear>
              </template>

              <!-- Column: Title + Base Year label -->
              <template #[`item.title`]="{ item }">
                <div v-if="item.title.length" class="text-capitalize">
                  <span class="year-title" :class="item.baseYear">{{
                    item.title
                  }}</span>
                </div>
                <div
                  v-if="item.baseYear.length && !item.base"
                  class="text-caption"
                >
                  <span class="course-base-year" :class="item.baseYear">
                    <span v-if="item.base">BASE: </span
                    >{{ interpret(item.baseYear) }}
                  </span>
                </div>
              </template>

              <!-- Column: Sections (popover listing) -->
              <template #[`item.sections`]="{ item }">
                <div v-if="item.sections.length">
                  <div class="text-center">
                    <v-menu location="bottom">
                      <template #activator="{ props }">
                        <v-btn variant="text" v-bind="props">
                          <v-icon
                            start
                            class="text-on-surface"
                            :icon="'mdi-eye'"
                          />
                          {{ item.sections.length }}
                        </v-btn>
                      </template>
                      <v-list>
                        <v-list-item v-for="(sec, i) in item.sections" :key="i">
                          <v-list-item-title>{{
                            lookUpSection(sec)
                          }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </div>
              </template>

              <!-- Column: Units count -->
              <template #[`item.units`]="{ item }">
                <div v-if="item.units.length">{{ item.units.length }}</div>
              </template>

              <!-- Column: Language -->
              <template #[`item.requiredLanguage`]="{ item }">
                <!-- Regular courses tab: show single required language -->
                <v-chip
                  size="small"
                  :color="getLanguageColor(item.requiredLanguage || 'en')"
                  variant="outlined"
                >
                  {{ (item.requiredLanguage || 'en').toUpperCase() }}
                </v-chip>
              </template>

              <!-- Column: Start Date -->
              <template #[`item.startDate`]="{ item }">
                <div v-if="item.startDate">{{ item.startDate }}</div>
              </template>

              <!-- Column: Status chip (click-to-verify for admins) -->
              <template #[`item.status`]="{ item }">
                <v-tooltip
                  :disabled="!checkIfLeader(item)"
                  location="top"
                  content-class="text-center"
                  max-width="500"
                >
                  <template #activator="{ props }">
                    <v-chip
                      v-bind="props"
                      :class="[
                        item.status == 'verified'
                          ? isDarkTheme
                            ? 'text-grey-lighten-3'
                            : 'text-black'
                          : isDarkTheme
                          ? 'text-black'
                          : 'text-grey-lighten-1',
                      ]"
                      :color="
                        item.status == 'verified'
                          ? 'rgba(165, 216, 103, .32)'
                          : ''
                      "
                      variant="flat"
                      @click.native="handleClick(item)"
                    >
                      <span v-if="item.base">Base Year</span>
                      <span v-else>{{ interpret(item.status) }}</span>
                    </v-chip>
                  </template>
                  <span v-if="item.status == 'verified'">
                    To view the course, click on the 'N' in the top left to
                    return to the home screen. Then, open the Courses drawer in
                    the top left.
                  </span>
                  <span v-else>
                    You will receive an email when your admin has approved your
                    course. Come back here and refresh the page.
                  </span>
                </v-tooltip>
              </template>

              <!-- Column: Group Name -->
              <template #[`item.selectedGroupName`]="{ item }">
                <div v-if="item.selectedGroupName">
                  {{ item.selectedGroupName }}
                </div>
              </template>

              <!-- Column: Row Actions (Edit / Archive / Delete) -->
              <template
                v-if="
                  isAuthenticated &&
                  checkAuthorization(['superAdmin', 'admin', 'leader'])
                "
                #[`item.actions`]="{ item }"
              >
                <div
                  v-if="!item.archive || checkAuthorization(['superAdmin'])"
                  class="action-icons"
                >
                  <!-- Edit -->
                      <v-icon
                        v-if="
                          !isHelpDesk() &&
                          (checkAuthorization(['superAdmin']) ||
                            (item.status == 'unverified' && checkIfLeader(item)))
                        "
                        @click.native="loadCourseToEdit(item)"
                        class="mr-6"
                        :icon="'edit'"
                      />

                  <!-- Delete (only superAdmin on archived) -->
                  <span
                    v-if="
                      item.archive &&
                      checkAuthorization(['superAdmin']) &&
                      !isHelpDesk()
                    "
                    class="action-icons"
                  >
                    <v-tooltip location="bottom">
                      <template #activator="{ props }">
                        <span v-bind="props">
                          <v-icon
                            @click.native="deleteCourse(item)"
                            class="delete-icon"
                            :icon="'delete_icon'"
                          />
                        </span>
                      </template>
                      <span>{{ $t('courses.deleteCourse') }}</span>
                    </v-tooltip>
                  </span>

                  <!-- Archive -->
                  <span v-else>
                    <v-tooltip
                      v-if="
                        checkIfLeader(item) ||
                        checkAuthorization(['superAdmin', 'admin'])
                      "
                      location="bottom"
                    >
                      <template #activator="{ props }">
                        <span v-bind="props">
                          <v-icon
                            v-if="!item.base"
                            @click.native="archiveCourse(item)"
                            class="archive-icon"
                            :icon="'logout'"
                          />
                          <span v-else class="px-2"></span>
                        </span>
                      </template>
                      <span>{{ $t('courses.archiveCourse') }}</span>
                    </v-tooltip>
                  </span>
                </div>
              </template>
            </v-data-table>
          </template>
          <!-- ======================== / COURSES TABLE ======================== -->
        </div>
      </v-flex>

      <!-- ===== Dialogs / Modals ===== -->
      <PreviewPopup
        v-model:showSectionInfo="showSectionInfo"
        :previewSectionInfoMeta="previewSectionInfoMeta"
        @listevent="handlerPopup"
      />
    </v-container>
  </div>
</template>

<script>
  import { firebase } from '@/firebase/init'
  import db from '@/firebase/init'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import PreviewPopup from '@/components/utilities/PreviewPopup'
  import Courses_Icon from '@/assets/icons/Courses_Icon.vue'
  import { currentTheme } from '@/plugins/vuetify'
  import { mapGetters } from 'vuex'
  import draggable from 'vuedraggable'
  import DefaultCourses from '@/components/utilities/DefaultCourses'
  import { confirmAction } from '@/components/utilities/SwalService'
  import Swal from 'sweetalert2'
  import { toYMD } from '@/helpers/stringUtils'
  import { useLanguages } from '@/helpers/useLanguages'
  import { matchesConfirmWord } from '@/helpers/confirmWord'

  export default {
    name: 'Courses',
    setup() {
      const { enabledLangs, defaultLang } = useLanguages()
      return { enabledLangs, defaultLang }
    },
    data() {
      return {
        activeTopicIndex: undefined,
        activeAuthorIndex: undefined,
        feedback: '',
        courseMsg:
          'Admins can only have one course for each year active at a time.<br> Leaders can only have one active course at a time.',
        backdoor: 0,
        courses: [],
        sections: [],
        isloading: true,
        isediting: false,
        search: '',
        groupSearch: '',
        filtered: '',
        activeindex: null,
        openCourseForm: false,
        editingCourse: false,
        courseTitle: '',
        startDate: toYMD(new Date()),
        menu: false,
        currPage: 1,
        pageSize: 30,
        selectedSections: [],
        selectedGroup: [],
        createdBy: '',
        theMission: [],
        iseditingCourse: false,
        courseToEdit: null,
        editStatus: '',
        groups: [],
        allGroups: [],
        defaultParams: [],
        showSectionInfo: false,
        previewSectionInfoMeta: null,
        allSectionUnits: [],
        allSectionCategories: [],
        openedPanel: [0, 1, 2],
        whichYear: '',
        numOfWeeks: '',
        topics: '',
        authors: '',
        filterLanguage: '',
        activeLanguageIndex: undefined,
        removedItem: '',
        addedItem: '',
        readyToRenderSections: false,
        isSubmitting: false,
        currentTab: 'regular', // 'base' or 'regular'
        headers: [
          { title: 'Course', sortable: false, key: 'title' },
          { title: 'Sections', sortable: false, key: 'sections' },
          { title: 'Units', sortable: false, key: 'units' },
          { title: 'Start Date', sortable: false, key: 'startDate' },
          { title: 'Status', sortable: false, key: 'status' },
          { title: 'Group', sortable: false, key: 'selectedGroupName' },
          {
            title: 'Actions',
            sortable: false,
            key: 'actions',
            align: 'right',
          }, //data-table-expand
        ],
        rules: {
          length: (len) => (v) =>
            (v || '').length >= len || `min ${len} characters required `,
          required: (v) => !!v || 'This field is required',
        },
        filters: [
          'A',
          'B',
          'C',
          'D',
          'E',
          'F',
          'G',
          'H',
          'I',
          'J',
          'K',
          'L',
          'M',
          'N',
          'O',
          'P',
          'Q',
          'R',
          'S',
          'T',
          'U',
          'V',
          'W',
          'X',
          'Y',
          'Z',
        ],
        /*********** MOVED THESE TO COMPONENT ***********/
        // years: {
        //     blueYear: [
        //         {'id' : 'HeyerkKYIVlyKm25uHIO', 'locked' : true},
        //         {'id' : 'RykRqngUBcsKHjsDTtHe'}
        //     ],
        //     greenYear: [
        //         {'id' : 'VZYsKCnzgVEy9v7TCxDV'},
        //         {'id' : 'hblTj6Qvpxf7sS2cTbdz', 'locked' : true}
        //     ],
        //     redYear: [
        //         {'id' : 'mnViHbwA2CLQkUR0UNht', 'locked' : true},
        //         {'id' : 'tEx5ybyiq6BZ67pBrNX1', 'locked' : true},
        //         {'id' : 'w1rCEyn6kGUXk16hNmeF'}
        //     ],
        // },
        /*********** ./ MOVED THESE TO COMPONENT ***********/
        baseYear: '',
        base: false,
        newBase: false,
        archive: false,
        list: [],
        list2: [],
        editable: true,
        isDragging: false,
        delayedDragging: false,
        areArchivedCoursesVisible: false,
        sectionsDialog: [],
        getMissions: [],
        requiredLanguage: null, // NEW: Required language for course
      }
    },
    components: {
      HowToUseThisPage,
      draggable,
      DefaultCourses,
      PreviewPopup,
      Courses_Icon,
    },
    computed: {
      churchid() {
        return this.$store.getters['AppData/cid']
      },
      ...mapGetters('UserData', ['isAuthenticated']),
      // ...mapGetters('AppData', ['getMissions']),
      editorBackgroundColor() {
        return currentTheme.value === 'dark' ? '#272727' : '#ffffff'
      },
      iconColor() {
        return currentTheme.value === 'dark' ? '#cecece' : '#383935'
      },
      isDarkTheme() {
        return currentTheme.value === 'dark'
      },
      dragOptions() {
        return {
          animation: 0,
          group: 'sections',
          disabled: !this.editable,
          ghostClass: 'ghost',
        }
      },
      authorizedActions() {
        if (this.isAuthenticated) {
          return this.editingCourse
            ? this.checkAuthorization(['superAdmin', 'admin']) &&
              this.editStatus !== 'verified'
              ? this.$t('courses.save')
              : this.$t('courses.edit')
            : this.checkAuthorization(['superAdmin', 'admin'])
            ? this.$t('courses.addCourse')
            : this.$t('courses.submitForApproval')
        }
        return false
      },
      totalWeeks() {
        let numWeeks = 0
        for (var i = 0; i < this.selectedSections.length; i++) {
          numWeeks += Object.keys(this.selectedSections[i].units).length
        }

        return numWeeks
      },
      weekCountsByLang() {
        if (!this.selectedSections || this.selectedSections.length === 0)
          return []

        // Count units per language
        const countsByLang = {}

        this.selectedSections.forEach((section) => {
          const sectionLang = section.requiredLanguage || 'en'
          const unitCount = (section.units || []).length

          if (!countsByLang[sectionLang]) {
            countsByLang[sectionLang] = 0
          }
          countsByLang[sectionLang] += unitCount
        })

        // Convert to array and sort by language code
        return Object.entries(countsByLang)
          .map(([lang, weeks]) => ({ lang, weeks }))
          .sort((a, b) => a.lang.localeCompare(b.lang))
      },
      visibleSelectedSectionsCount() {
        return (this.selectedSections || []).filter((section) =>
          this.matchesSelectedLanguageFilter(section)
        ).length
      },
      isSharedBaseTemplateEdit() {
        return !!(this.editingCourse && this.base)
      },
      uniqueWeeks() {
        const set = new Set(
          (this.list || [])
            .filter((item) => this.matchesAvailableFiltersExceptWeeks(item))
            .map((item) => (item.units ? item.units.length : 0))
            .filter((n) => n > 0)
        )
        return Array.from(set).sort((a, b) => a - b)
      },
      haveMission() {
        let hasMission = null
        if (this.getMissions.length > 0) {
          const requiredLang = (this.requiredLanguage || 'en').toLowerCase()
          // console.log('1 ', this.getMissions)
          hasMission = this.getMissions.filter((item) => {
            const missionLang = (item.requiredLanguage || 'en').toLowerCase()
            return (
              item.weeks.length === this.totalWeeks && missionLang === requiredLang
            )
          })
        }
        // console.log('1 ', hasMission)
        return hasMission
      },
      missionLengthsByLanguage() {
        const lengthsByLang = {}
        ;(this.getMissions || []).forEach((mission) => {
          const missionLang = (mission.requiredLanguage || 'en').toLowerCase()
          const missionLength = (mission.weeks || []).length
          if (!Number.isFinite(missionLength) || missionLength <= 0) return
          if (!lengthsByLang[missionLang]) lengthsByLang[missionLang] = new Set()
          lengthsByLang[missionLang].add(missionLength)
        })

        return Object.fromEntries(
          Object.entries(lengthsByLang).map(([lang, lengths]) => [
            lang,
            Array.from(lengths).sort((a, b) => a - b),
          ])
        )
      },
      missingMissionRequirements() {
        if (!this.isSharedBaseTemplateEdit) return []

        return this.weekCountsByLang
          .map(({ lang, weeks }) => {
            const available = this.missionLengthsByLanguage[lang] || []
            const hasExactMatch = available.includes(weeks)
            return hasExactMatch ? null : { lang, weeks, available }
          })
          .filter(Boolean)
      },
      hasValidMissionCoverage() {
        if (this.isSharedBaseTemplateEdit) {
          return this.missingMissionRequirements.length === 0
        }
        return !!(this.haveMission && this.haveMission.length > 0)
      },
      availableMissionWeekCounts() {
        const requiredLang = (this.requiredLanguage || 'en').toLowerCase()
        const counts = (this.getMissions || [])
          .filter((m) => (m.requiredLanguage || 'en').toLowerCase() === requiredLang)
          .map((m) => (m.weeks || []).length)
          .filter((n) => Number.isFinite(n) && n > 0)
        return Array.from(new Set(counts)).sort((a, b) => a - b)
      },
      filterCourses() {
        const getUser = this.$store.getters['UserData/getUser']

        return this.courses.filter((item) => {
          if (this.checkAuthorization(['leader'])) {
            return item.createdBy === getUser.uid
          }
          return item
        })
      },
      visibleCourses() {
        const courses = Array.isArray(this.filterCourses)
          ? this.filterCourses
          : []
        const nonArchived = this.areArchivedCoursesVisible
          ? courses
          : courses.filter((c) => !c.archive)

        // Only show tabs/filtering for superAdmin
        const isSuperAdmin = this.checkAuthorization(['superAdmin'])
        let filtered
        if (isSuperAdmin) {
          if (this.currentTab === 'base') {
            filtered = nonArchived.filter((c) => c.base === true)
          } else {
            filtered = nonArchived.filter((c) => !c.base)
          }
        } else {
          filtered = nonArchived.filter((c) => !c.base)
        }

        // Derive display field without mutating course objects — mutating `courses` in a
        // computed retriggers DefaultCourses' deep `courses` watch and freezes the UI.
        return filtered.map((item) => {
          const selectedGroupName =
            item.selectedGroup?.length > 0
              ? this.lookUpGroup(item.selectedGroup[0].id) || ''
              : item.selectedGroupName || ''
          return { ...item, selectedGroupName }
        })
      },
      baseCoursesHeaders() {
        // Base courses: COURSE, SECTIONS, UNITS, LANG, ACTIONS
        return [
          {
            title: this.$t('courses.tableHeaders.course'),
            sortable: true,
            key: 'title',
          },
          {
            title: this.$t('courses.tableHeaders.sections'),
            sortable: true,
            key: 'sections',
            width: '125px',
          },
          {
            title: this.$t('courses.tableHeaders.units'),
            sortable: true,
            key: 'units',
            width: '100px',
          },
          {
            title: this.$t('courses.tableHeaders.lang'),
            sortable: true,
            key: 'requiredLanguage',
            width: '120px',
          },
          {
            title: this.$t('courses.tableHeaders.actions'),
            sortable: false,
            key: 'actions',
            align: 'right',
          },
        ]
      },
      regularCoursesHeaders() {
        // Regular courses: All columns
        let h = [
          {
            title: this.$t('courses.tableHeaders.course'),
            sortable: true,
            key: 'title',
          },
          {
            title: this.$t('courses.tableHeaders.sections'),
            sortable: true,
            key: 'sections',
            width: '125px',
          },
          {
            title: this.$t('courses.tableHeaders.units'),
            sortable: true,
            key: 'units',
            width: '100px',
          },
          {
            title: this.$t('courses.tableHeaders.lang'),
            sortable: true,
            key: 'requiredLanguage',
            width: '80px',
          },
          {
            title: this.$t('courses.startDate'),
            sortable: false,
            key: 'startDate',
            width: '125px',
          },
          {
            title: this.$t('courses.tableHeaders.status'),
            sortable: true,
            key: 'status',
          },
          {
            title: this.$t('courses.tableHeaders.group'),
            sortable: true,
            key: 'selectedGroupName',
          },
          {
            title: this.$t('courses.tableHeaders.actions'),
            sortable: false,
            key: 'actions',
            align: 'right',
          },
        ]
        if (this.isAuthenticated) {
          if (this.checkAuthorization(['admin']))
            h = [
              {
                title: this.$t('courses.tableHeaders.course'),
                sortable: true,
                key: 'title',
              },
              {
                title: this.$t('courses.tableHeaders.sections'),
                sortable: true,
                key: 'sections',
                width: '125px',
              },
              {
                title: this.$t('courses.tableHeaders.units'),
                sortable: true,
                key: 'units',
                width: '100px',
              },
              {
                title: this.$t('courses.tableHeaders.lang'),
                sortable: true,
                key: 'requiredLanguage',
                width: '80px',
              },
              {
                title: this.$t('courses.startDate'),
                sortable: false,
                key: 'startDate',
                width: '125px',
              },
              {
                title: this.$t('courses.tableHeaders.status'),
                sortable: true,
                key: 'status',
              },
              {
                title: this.$t('courses.tableHeaders.group'),
                sortable: true,
                key: 'selectedGroupName',
              },
              {
                title: this.$t('courses.tableHeaders.actions'),
                sortable: false,
                key: 'actions',
                align: 'right',
              },
            ]
        }
        return h
      },
      availableLanguages() {
        return this.enabledLangs.map((lang) => ({
          code: lang.code,
          label: lang.label,
        }))
      },
      availableSections() {
        // Base courses show all sections regardless of language
        if (this.base && this.editingCourse) {
          return this.sections
        }
        // Regular courses filter by selected language
        if (!this.requiredLanguage) {
          return this.sections
        }
        return this.sections.filter((section) => {
          // Check if section has the required language
          return (
            section.requiredLanguage === this.requiredLanguage ||
            (!section.requiredLanguage && this.requiredLanguage === 'en')
          )
        })
      },
      sectionAuthors() {
        let authors = this.availableSections.map(function (obj) {
          return obj.authorName
        })
        authors = authors.filter(function (v, i) {
          return authors.indexOf(v) == i
        })
        authors.sort()
        return authors
      },
      filteredGroups() {
        return this.groups.filter((item) => {
          //use this to search ONLY first word in string
          const first = item.name.split(' ')[0]

          if (this.groupSearch) {
            return item.name.toLowerCase().match(this.groupSearch.toLowerCase())
          }
          return first.toLowerCase().startsWith(this.filtered)
        })
      },
      displayGroups() {
        const trimStart = (this.currPage - 1) * this.pageSize
        const trimEnd = trimStart + this.pageSize
        return this.filteredGroups.slice(trimStart, trimEnd)
      },
      featuredSections() {
        return this.sections.filter((value) => value.featured)
      },
      alreadyHasCourse() {
        // check to see if the user is a Pastor
        const userRoles = this.$store.getters['UserData/getRoles']
        const getUser = this.$store.getters['UserData/getUser']

        if (userRoles !== null) {
          const isAdmin = userRoles.includes('admin')

          if (isAdmin) {
            // Admins can create one base course per language/year combo
            return false
          }

          if (!isAdmin) {
            // check to see if user is a leader and has already created a course
            for (let index = 0; index < this.courses.length; index++) {
              if (
                this.courses[index].selectedGroup &&
                this.courses[index].selectedGroup.length > 0
              ) {
                // const getUser = this.$store.getters['UserData/getUser']
                const grp = this.groups.filter(
                  (g) =>
                    this.courses[index].archive === false &&
                    g.group_doc_id == this.courses[index].selectedGroup[0].id
                )
                if (grp.length > 0)
                  if (grp[0].leader === getUser.uid) {
                    // this.courseMsg = 'Leaders can only have one active course at a time'
                    return true
                  }
              }
            }
          } else {
            // if Admin count the number of active courses. They can only have three active. one from each year
            const count = this.courses.filter(
              (obj) => obj.archive === false && obj.createdBy === getUser.uid
            ).length
            if (count >= 3) {
              // this.courseMsg = 'Admins can only have one course for each year active at a time'
              return true
            }
          }
        }

        return false
      },
    },
    watch: {
      churchid: {
        deep: true,
        handler(event) {
          // Sign-out clears the church; there is nothing to load then, and the
          // queries would be refused (firestore.rules).
          if (!event) return
          this.getCourses(event)
        },
      },
      isDragging(newValue) {
        if (newValue) {
          this.delayedDragging = true
          return
        }
        this.$nextTick(() => {
          this.delayedDragging = false
        })
      },
      uniqueWeeks(newList) {
        if (this.numOfWeeks && !newList.includes(this.numOfWeeks)) {
          this.numOfWeeks = ''
        }
      },
      requiredLanguage: {
        handler(newLang, oldLang) {
          // Only trigger confirmation if:
          // 1. We have both old and new values (not initial load)
          // 2. The language actually changed
          // 3. There are selected sections to clear
          if (
            oldLang != null &&
            newLang != null &&
            oldLang !== newLang &&
            this.selectedSections.length > 0
          ) {
            if (confirm(this.$t('courses.languageChangeWarning'))) {
              this.selectedSections = []
              this.list = this.availableSections
              // Reset DefaultCourses if needed
              if (this.defaultParams && this.defaultParams.selectedSections) {
                this.defaultParams.selectedSections = []
              }
            } else {
              // Revert the language change
              this.$nextTick(() => {
                this.requiredLanguage = oldLang
              })
            }
          } else if (newLang != null) {
            // Filter available sections when language is selected
            this.list = this.availableSections.filter(
              (s) => !this.selectedSections.some((sel) => sel.id === s.id)
            )
          }
        },
      },
      filterLanguage(newLang) {
        const index = this.availableLanguages.findIndex(
          (lang) => lang.code === newLang
        )
        this.activeLanguageIndex = index >= 0 ? index : undefined
      },
    },
    methods: {
      setBaseTemplateLanguageFilter(languageCode) {
        this.filterLanguage = languageCode || ''
      },
      handleClick(item) {
        if (!this.checkAuthorization(['superAdmin', 'admin'])) {
          return // Do nothing if user is not authorized
        }
        this.verifyCourse(item)
      },
      interpret(word) {
        if (word === 'verified') return 'Approved'
        if (word === 'unverified') return 'Needs Approval'
        if (word === 'greenYear') return 'The Green Year'
        if (word === 'redYear') return 'The Red Year'
        if (word === 'blueYear') return 'The Blue Year'
        return word
      },
      yearLabel(value) {
        if (value === 'inBlue') return 'Blue Year'
        if (value === 'inRed') return 'Red Year'
        if (value === 'inGreen') return 'Green Year'
        return value
      },
      onPickDate(val) {
        this.startDate = toYMD(val) // guarantees 'YYYY-MM-DD'
      },
      listChanged(value) {
        if (value.removed) {
          const exists =
            this.list.findIndex(
              (element) => element.id === value.removed.element.id
            ) > -1
          if (exists) {
            for (var j = 0; j < this.list.length; j++) {
              if (value.removed.element.id === this.list[j].id) {
                this.list.splice(j, 1)
                break
              }
            }
          }
        }
        if (value.added) {
          const exists =
            this.list.findIndex(
              (element) => element.id === value.added.element.id
            ) > -1
          if (!exists) {
            this.list.push(value.added.element)
          }
        }
        // this.search = ''
        // this.whichYear = ''
        // this.numOfWeeks = ''
        // this.topics = ''
        // this.authors = ''
        // this.activeTopicIndex = undefined
        // this.activeAuthorIndex = undefined
      },
      async verifyCourse(course) {
        this.feedback = ''

        if (course) {
          const confirmed = await confirmAction({
            title: this.$t('courses.verifyTitle', {
              title: course.title,
            }),
            html: this.$t('courses.verifyInstruction'),
            input: 'text',
            inputPlaceholder: this.$t('courses.verifyPlaceholder'),
            inputValidator: (value) => {
              return new Promise((resolve) => {
                if (
                  matchesConfirmWord(
                    value,
                    'VERIFY',
                    this.$t('courses.verifyPlaceholder')
                  )
                ) {
                  resolve()
                } else {
                  resolve(this.$t('courses.verifyValidation'))
                }
              })
            },
            confirmButtonText: this.$t('courses.verifyConfirm'),
            cancelButtonText: this.$t('courses.cancelKeep'),
            context: this,
            buttonsStyling: false,
            customClass: {
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
          })

          if (confirmed) {
            await db.collection('courses').doc(course.id).update({
              status: 'verified',
            })
            this.feedback = `${course.title} has been verified`
            this.getCourses(this.$store.getters['AppData/cid'])
          }
        } else {
          Swal.fire({
            title: 'Houston we have a problem.',
            text: 'Error with the verify function, missing params.',
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
        }
      },
      async addCourse() {
        // prevent double submit
        if (this.isSubmitting) return
        this.feedback = ''

        const userRoles = this.$store.getters['UserData/getRoles'] || []
        const getUser = this.$store.getters['UserData/getUser']

        // 1) Normalize title/date
        const title = (this.courseTitle || '').trim()
        if (!title) {
          this.feedback = this.$t('courses.feedbackMissingTitle')
          return
        }

        // auto-fill startDate for admins only if blank
        if (userRoles.includes('admin') && !this.startDate) {
          this.startDate = toYMD(new Date())
        }
        const startDate = this.startDate ? toYMD(this.startDate) : ''
        if (!startDate) {
          this.feedback = this.$t('courses.feedbackMissingTitle')
          return
        }

        // 2) Selected group resolution
        let selectedGroupID = []
        const isLeader = userRoles.includes('leader')

        if (isLeader) {
          const leaderGroup = this.groups.find((g) => g.leader === getUser?.uid)
          if (leaderGroup) selectedGroupID = [{ id: leaderGroup.group_doc_id }]
        } else if (this.selectedGroup?.length > 0) {
          selectedGroupID = [{ id: this.selectedGroup[0].group_doc_id }]
        }

        // 3) Sections and Units payloads
        // collapse duplicates by id just in case
        const byId = new Map()
        ;(this.selectedSections || []).forEach((s) => {
          if (!s || !s.id) return
          // last one wins (order preserved in array anyway)
          byId.set(String(s.id), s)
        })
        const uniqueSections = Array.from(byId.values())

        const sectionsPayload = uniqueSections.map((s) => ({
          id: s.id,
          locked: !!s.locked,
          superLocked: !!s.superLocked,
        }))

        const unitsPayload = uniqueSections
          .flatMap((s) => s.units || [])
          .map((u) => ({ id: u.id }))

        // 4) Status
        const status = this.checkAuthorization(['superAdmin', 'admin'])
          ? 'verified'
          : 'unverified'

        // 5) Mission selection: prefer custom for this church/length, else default for that length
        const requiredLang = (this.requiredLanguage || 'en').toLowerCase()
        const hasMission = (this.getMissions || []).filter((m) => {
          const missionLang = (m.requiredLanguage || 'en').toLowerCase()
          return (m.weeks || []).length === this.totalWeeks && missionLang === requiredLang
        })
        if (hasMission.length === 0) {
          this.feedback = this.$t('courses.feedbackNoMission')
          return
        }

        let mission
        if (hasMission.length === 1) {
          mission = hasMission[0]
        } else {
          const custom = hasMission.find(
            (m) => m.cid && m.cid === this.churchid
          )
          const fallbackDefault = hasMission.find((m) => !m.cid)
          mission = custom || fallbackDefault || hasMission[0]
        }
        const missionArr = mission ? [mission] : []

        // 6) Validate required language
        if (!this.requiredLanguage) {
          this.feedback = this.$t('courses.feedbackMissingLanguage')
          this.isSubmitting = false
          return
        }

        const normalizedLang = (this.requiredLanguage || 'en').toLowerCase()
        const languageLabel =
          this.availableLanguages.find(
            (l) => l.code === this.requiredLanguage
          )?.label || this.requiredLanguage.toUpperCase()

        const isBaseCourse = selectedGroupID.length === 0
        if (isBaseCourse) {
          const normalizedBaseYear = (this.baseYear || '').toString()
          const existingBaseCourse = (this.courses || []).some((course) => {
            if (course.archive) return false
            if ((course.selectedGroup || []).length > 0) return false
            const courseLang = (course.requiredLanguage || 'en').toLowerCase()
            const courseBaseYear = (course.baseYear || '').toString()
            const courseCid = course.cid || ''
            if (courseCid !== (this.churchid || '')) return false
            if (courseLang !== normalizedLang) return false
            return courseBaseYear === normalizedBaseYear
          })

          if (existingBaseCourse) {
            const baseExistsMessage =
              this.$te && this.$te('courses.feedbackBaseExists')
                ? this.$t('courses.feedbackBaseExists', {
                    language: languageLabel,
                    year: this.interpret(this.baseYear),
                  })
                : `A base course already exists for ${languageLabel} - ${this.interpret(
                    this.baseYear
                  )}.`

            this.feedback = baseExistsMessage
            this.isSubmitting = false
            return
          }
        }

        // 7) Validate all selected sections match required language
        const mismatchedSections = this.selectedSections.filter((section) => {
          return (
            section.requiredLanguage !== this.requiredLanguage &&
            !(!section.requiredLanguage && this.requiredLanguage === 'en')
          )
        })

        if (mismatchedSections.length > 0) {
          const languageLabel =
            this.availableLanguages.find(
              (l) => l.code === this.requiredLanguage
            )?.label || this.requiredLanguage.toUpperCase()

          const confirmed = await confirmAction({
            title: this.$t('courses.sectionsMismatchWarning', {
              language: languageLabel,
            }),
            html: `${
              mismatchedSections.length
            } section(s) will be removed: ${mismatchedSections
              .map((s) => s.title)
              .join(', ')}`,
            confirmButtonText: 'Continue',
            cancelButtonText: 'Cancel',
            context: this,
          })

          if (!confirmed) {
            this.isSubmitting = false
            return
          }

          // Remove mismatched sections
          this.selectedSections = this.selectedSections.filter((section) => {
            return (
              section.requiredLanguage === this.requiredLanguage ||
              (!section.requiredLanguage && this.requiredLanguage === 'en')
            )
          })
        }

        // 8) Persist
        const payload = {
          sections: sectionsPayload,
          units: unitsPayload,
          title,
          baseYear: this.baseYear || '',
          cid: this.churchid || '',
          startDate,
          selectedGroup: selectedGroupID,
          createdBy: getUser?.uid || '',
          status,
          archive: false,
          mission: missionArr,
          requiredLanguage: this.requiredLanguage || 'en', // NEW: Save required language
        }

        this.isSubmitting = true
        try {
          await db.collection('courses').add(payload)

          // Notify admin
          this.$store.dispatch('AppData/notification', {
            leader: getUser,
            meta: { courseTitle: title },
            type: 'Course created',
            cid: this.$store.getters['AppData/cid'],
          })

          // Reset form state
          this.selectedSections = []
          this.courseTitle = ''
          this.requiredLanguage = null // NEW: Reset language
          this.openCourseForm = false

          // Reload courses
          this.getCourses(this.churchid)
        } catch (error) {
          console.error('🔥 Error storing course in Firestore:', error)
          this.$store.dispatch('AppData/logError', {
            message: 'Error storing course in Firestore',
            stack: error.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'Courses.vue',
            function: 'addCourse',
            additionalInfo: {
              courseTitle: title,
              churchID: this.churchid,
              userId: getUser ? getUser.uid : 'unknown',
              userEmail: getUser ? getUser.email : 'unknown',
            },
          })
          this.feedback = 'Error: Unable to store course. Please try again.'
        } finally {
          this.isSubmitting = false
        }
      },
      addNewHandler() {
        this.feedback = ''
        // this backdoor is used to trigger the unitsList computed property
        this.backdoor++

        this.readyToRenderSections = false
        this.courseTitle = ''
        this.selectedSections = []
        this.requiredLanguage = null // NEW: Reset language
        this.filterLanguage = ''
        this.activeLanguageIndex = undefined
        if (this.editingCourse) {
          if (this.$refs.addeditform) this.$refs.addeditform.reset()
          this.openCourseForm = true
        } else {
          this.openCourseForm = !this.openCourseForm
        }
        this.editingCourse = false
        this.startDate = ''

        // reset the groups
        this.groups.forEach((x) => {
          x.checked = false
          x.disabled = false
        })

        // make sure all assigned groups are disabled
        this.groups.filter((item) => {
          const allSelectedGroups = this.allGroups.find(
            (s) => s.id == item.group_doc_id
          )

          if (allSelectedGroups) {
            item.hasCourse = true
            item.disabled = true
          }
        })
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const normalizedPermissions = permissions.includes('superAdmin')
            ? [...permissions, 'helpDesk']
            : permissions

          const hasRole = normalizedPermissions.filter(() =>
            userRoles.some((role) => normalizedPermissions.includes(role))
          )
          if (hasRole && hasRole.length > 0) return true
        }
        return false
      },
      isHelpDesk() {
        const userRoles = this.$store.getters['UserData/getRoles']
        return Array.isArray(userRoles) && userRoles.includes('helpDesk')
      },
      getLanguageColor(languageCode) {
        const colors = {
          en: 'blue',
          pt: 'green',
          es: 'orange',
          fr: 'purple',
          de: 'red',
        }
        return colors[languageCode] || 'grey'
      },
      getCourseLanguages(course) {
        // For base courses, get unique languages from all sections
        if (!course.base || !course.sections || course.sections.length === 0) {
          return []
        }

        const languages = new Set()

        // Look up each section and get its language
        course.sections.forEach((sectionRef) => {
          const sectionId = sectionRef.id || sectionRef
          const section = this.sections.find((s) => s.id === sectionId)
          if (section) {
            const lang = section.requiredLanguage || 'en'
            languages.add(lang)
          }
        })

        // Convert to array and sort
        return Array.from(languages).sort()
      },
      checkIfLeader(item) {
        if (item.selectedGroup && item.selectedGroup.length > 0) {
          const getUser = this.$store.getters['UserData/getUser']
          const grp = this.groups.filter(
            (g) => g.group_doc_id == item.selectedGroup[0].id
          )
          if (grp.length > 0) if (grp[0].leader === getUser.uid) return true
        }

        return false
      },
      courseFilters(which) {
        if (which == 'show-archived') {
          this.areArchivedCoursesVisible = !this.areArchivedCoursesVisible
        }
        // if(which == 'clear') {
        //     this.getCourses(this.$store.getters['AppData/cid'])
        // }
      },
      checkForDefaultYear(item) {
        const allYears = this.defaultParams?.allYears

        if (
          !allYears ||
          !['blueYear', 'greenYear', 'redYear'].every((k) =>
            Array.isArray(allYears[k])
          )
        ) {
          // Early bail: no data yet
          return []
        }

        const theClass = []

        const inBlue = allYears.blueYear.some(
          (obj) => String(obj.id) === String(item.id)
        )
        const inGreen = allYears.greenYear.some(
          (obj) => String(obj.id) === String(item.id)
        )
        const inRed = allYears.redYear.some(
          (obj) => String(obj.id) === String(item.id)
        )

        if (inBlue) theClass.push('inBlue')
        if (inGreen) theClass.push('inGreen')
        if (inRed) theClass.push('inRed')

        return theClass
      },

      // alreadyHasCourse() {
      //     console.log('1')
      //     // check to see if the user is a Pastor
      //     const userRoles = this.$store.getters['UserData/getRoles']
      //     const getUser = this.$store.getters['UserData/getUser']

      //     if(userRoles !== null) {
      //         const isAdmin = userRoles.includes('admin')

      //         if(!isAdmin) {
      //             // check to see if user is a leader and has already created a course
      //             for (let index = 0; index < this.courses.length; index++) {
      //                 if(this.courses[index].selectedGroup && this.courses[index].selectedGroup.length > 0) {
      //                     console.log('2')
      //                     // const getUser = this.$store.getters['UserData/getUser']
      //                     const grp = this.groups.filter(g => (this.courses[index].archive === false && g.group_doc_id == this.courses[index].selectedGroup[0].id))
      //                     if(grp.length > 0)
      //                         if(grp[0].leader === getUser.uid) {
      //                             this.courseMsg = 'Leaders can only have one active course at a time'
      //                             return true
      //                         }
      //                 }
      //             }
      //         } else {
      //             // if Admin count the number of active courses. They can only have three active. one from each year
      //             const count = this.courses.filter((obj) => (obj.archive === false && obj.createdBy === getUser.uid)).length
      //             if(count >= 3) {
      //                 this.courseMsg = 'Admins can only have one course for each year active at a time'
      //                 return true
      //             }
      //         }
      //     }

      //     return false
      // },
      closebuilder() {
        // this backdoor is used to trigger the unitsList computed property
        this.backdoor++

        this.courseTitle = ''
        this.base = false // Reset base flag
        this.filterLanguage = ''
        this.activeLanguageIndex = undefined
        if (this.editingCourse) {
          if (this.$refs.addeditform) this.$refs.addeditform.reset()
        }
        this.openCourseForm = false
        this.editingCourse = false
        this.readyToRenderSections = false
      },
      handlerDefaultCourses(params) {
        this.defaultParams = params

        if (this.editingCourse) {
          console.log('[Edit Mode] Skipping selectedSections overwrite')
        } else {
          console.log('[New Course] Using default selectedSections')
          // Filter selected sections by required language if set
          let sections = params.selectedSections
          if (this.requiredLanguage) {
            sections = sections.filter(
              (s) =>
                s.requiredLanguage === this.requiredLanguage ||
                (!s.requiredLanguage && this.requiredLanguage === 'en')
            )
          }
          this.selectedSections = sections
          this.newBase = !sections.length
        }

        this.baseYear = params.baseYear
        // Filter available sections list by language if requiredLanguage is set
        let list = params.list
        if (this.requiredLanguage) {
          list = list.filter(
            (s) =>
              s.requiredLanguage === this.requiredLanguage ||
              (!s.requiredLanguage && this.requiredLanguage === 'en')
          )
        }
        this.list = list.filter(
          (s) => !this.selectedSections.some((sel) => sel.id === s.id)
        )
        this.readyToRenderSections = true
      },
      sendToCurriculum() {
        window.open('https://thejourneycurriculum.com/curriculum/', '_blank')
      },
      row_classes(wrappedItem) {
        const course = wrappedItem.item
        const classes = []

        if (course.disabled) classes.push('disabled')
        if (course.base) classes.push('base')

        return classes.join(' ')
      },
      featuredSectionCategory(section) {
        if (section.category[0]) {
          const category = this.allSectionCategories.find(
            (c) => c.id == section.category[0].id
          )
          return category.name
        }
        return
      },
      lookUpSection(section) {
        // console.log('2 ', section.id)
        const sectionName = this.sections.filter(
          (item) => item.id === section.id
        )
        if (sectionName.length > 0) return sectionName[0].title

        return false
      },
      /*********** MOVED THESE TO COMPONENT ***********/
      // defaultSections(year) {
      //     // this.selectedSections = year
      //     this.baseYear = year
      //     this.selectedSections = this.sections.filter((item) =>{

      //         // return this.blueYear.find(u => u.id == item.id)
      //         return this.years[year].find(u => {

      //             if(u.id == item.id) {
      //                 item.superLocked = u.locked
      //                 return item
      //             }

      //         })

      //     })

      //     this.list = this.sections.filter( ( el ) => !this.selectedSections.includes( el ) )

      //     this.list.map(function(item) {
      //         item.locked = false
      //         item.superLocked = false
      //         return item
      //     })

      // },
      /*********** ./ MOVED THESE TO COMPONENT ***********/
      async archiveCourse(course) {
        this.feedback = ''

        if (course) {
          const confirmed = await confirmAction({
            title: this.$t('courses.archiveTitle', {
              title: course.title,
            }),
            html: this.$t('courses.archiveInstruction'),
            input: 'text',
            inputPlaceholder: this.$t('courses.archivePlaceholder'),
            inputValidator: (value) => {
              return new Promise((resolve) => {
                if (
                  matchesConfirmWord(
                    value,
                    'ARCHIVE',
                    this.$t('courses.archivePlaceholder')
                  )
                ) {
                  resolve()
                } else {
                  resolve(this.$t('courses.archiveValidation'))
                }
              })
            },
            confirmButtonText: this.$t('courses.archiveConfirm'),
            cancelButtonText: this.$t('courses.cancelKeep'),
            context: this,
            buttonsStyling: false,
            customClass: {
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
          })

          if (confirmed) {
            await db.collection('courses').doc(course.id).update({
              archive: true,
            })

            // archive all currentcourses for the course
            const collections = ['currentcourses']
            for (const element of collections) {
              let elementsRef = db
                .collection(element)
                .where('cid', '==', course.cid)
                .where('courseID', '==', course.id)

              const snapshot = await elementsRef.get()
              const batch = db.batch()

              snapshot.forEach((doc) => {
                batch.update(doc.ref, { status: 'archived' })
              })

              await batch.commit()
            }

            this.feedback = this.$t('courses.courseArchived', {
              title: course.title,
            })
            this.getCourses(this.$store.getters['AppData/cid'])
          }
        } else {
          Swal.fire({
            title: 'Houston we have a problem.',
            text: 'Error with the archive function, missing params.',
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
        }
      },
      async deleteCourse(course) {
        this.feedback = ''

        if (this.isHelpDesk && this.isHelpDesk()) {
          const key = 'courses.feedbackHelpDeskNoDelete'
          this.feedback =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot delete courses.'
          return
        }

        if (course) {
          const confirmed = await confirmAction({
            title: this.$t('courses.deleteTitle', {
              title: course.title,
            }),
            html: this.$t('courses.deleteInstruction'),
            input: 'text',
            inputPlaceholder: this.$t('courses.deletePlaceholder'),
            inputValidator: (value) => {
              return new Promise((resolve) => {
                if (
                  matchesConfirmWord(
                    value,
                    'DELETE',
                    this.$t('courses.deletePlaceholder')
                  )
                ) {
                  resolve()
                } else {
                  resolve(this.$t('courses.deleteValidation'))
                }
              })
            },
            confirmButtonText: this.$t('courses.deleteConfirm'),
            cancelButtonText: this.$t('courses.cancelKeep'),
            context: this,
            buttonsStyling: false,
            customClass: {
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
          })

          if (confirmed) {
            await db.collection('courses').doc(course.id).delete()
            this.feedback = this.$t('courses.courseDeleted', {
              title: course.title,
            })
            this.getCourses(this.$store.getters['AppData/cid'])
          }
        } else {
          Swal.fire({
            title: 'Houston we have a problem.',
            text: 'Error with the delete function, missing params.',
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
        }
      },
      editCourse() {
        // get the user id for the createdBy field
        // const getUser = this.$store.getters['UserData/getUser']

        this.iseditingCourse = true

        if (!this.hasValidMissionCoverage) {
          this.feedback = this.isSharedBaseTemplateEdit
            ? 'Each language in this shared base template must have a mission with the same number of weeks.'
            : this.$t('courses.feedbackNoMission')
          this.iseditingCourse = false
          return
        }

        if (this.courseTitle && this.courseToEdit) {
          setTimeout(() => {
            // get the id of the selected group
            let selectedGroupID = []
            if (this.selectedGroup.length > 0) {
              selectedGroupID.push({
                id: this.selectedGroup[0].group_doc_id,
              })
            }

            const isBaseCourseEdit = selectedGroupID.length === 0 && !this.base
            if (isBaseCourseEdit) {
              const normalizedLang = (this.requiredLanguage || 'en').toLowerCase()
              const languageLabel =
                this.availableLanguages.find(
                  (l) => l.code === this.requiredLanguage
                )?.label || this.requiredLanguage.toUpperCase()
              const normalizedBaseYear = (this.baseYear || '').toString()

              const duplicateBase = (this.courses || []).some((course) => {
                if (course.archive) return false
                if (!course.id || course.id === this.courseToEdit.id) return false
                if ((course.selectedGroup || []).length > 0) return false
                const courseLang = (course.requiredLanguage || 'en').toLowerCase()
                const courseBaseYear = (course.baseYear || '').toString()
                const courseCid = course.cid || ''
                if (courseCid !== (this.churchid || '')) return false
                if (courseLang !== normalizedLang) return false
                return courseBaseYear === normalizedBaseYear
              })

              if (duplicateBase) {
                const baseExistsMessage =
                  this.$te && this.$te('courses.feedbackBaseExists')
                    ? this.$t('courses.feedbackBaseExists', {
                        language: languageLabel,
                        year: this.interpret(this.baseYear),
                      })
                    : `A base course already exists for ${languageLabel} - ${this.interpret(
                        this.baseYear
                      )}.`

                this.feedback = baseExistsMessage
                this.iseditingCourse = false
                return
              }
            }

            // get the id of each selected section
            let listOfSections = Object.keys(this.selectedSections).map(
              (key) => {
                return this.selectedSections[key]
              }
            )

            // console.log('2 ', this.selectedSections)

            let listOfSectionIDs = []

            if (listOfSections.length) {
              for (let index = 0; index < listOfSections.length; index++) {
                const section = listOfSections[index]
                // add new unit IDs, locked, superLocked to the section unit array
                if (this.checkAuthorization(['superAdmin'])) {
                  listOfSectionIDs.push({
                    id: section.id,
                    locked: section.locked ? true : false,
                    superLocked:
                      section.superLocked || section.locked ? true : false,
                  })
                } else {
                  listOfSectionIDs.push({
                    id: section.id,
                    locked: section.locked ? true : false,
                    superLocked: section.superLocked ? true : false,
                  })
                }
              }
            }

            // make an array of all the units
            let sectionUnits = []
            for (var i = 0; i < this.selectedSections.length; i++) {
              sectionUnits.push(this.selectedSections[i].units)
            }
            // flatten the arrays to one array
            let allUnits = sectionUnits.reduce(
              (prev, next) => prev.concat(next),
              []
            )

            let listOfUnitIDs = []
            // loop through the properties and create a new array of unit IDs
            if (allUnits.length) {
              for (let index = 0; index < allUnits.length; index++) {
                const unit = allUnits[index]
                // add new unit IDs to the section unit array
                listOfUnitIDs.push({
                  id: unit.id,
                })
              }
            }

            let status = this.checkAuthorization(['superAdmin', 'admin'])
              ? 'verified'
              : 'unverified'
            let churchID = this.base ? '' : this.churchid

            let data = {
              sections: listOfSectionIDs,
              cid: churchID,
              units: listOfUnitIDs,
              title: this.courseTitle,
              baseYear: this.baseYear,
              startDate: this.startDate,
              selectedGroup: selectedGroupID,
              createdBy: this.createdBy,
              status: status,
              archive: this.archive,
              base: this.base,
              mission: this.theMission,
              requiredLanguage: this.requiredLanguage || 'en', // NEW: Save required language
            }

            data = this.removeUndefinedFields(data)

            db.collection('courses')
              .doc(this.courseToEdit.id)
              .set(data)
              .then(() => {
                // Send Notification to Admin
                this.$store.dispatch('AppData/notification', {
                  leader: this.createdBy,
                  meta: {
                    courseTitle: this.courseTitle,
                  },
                  type: 'Course approved',
                  cid: this.$store.getters['AppData/cid'],
                })

                // Reset fields
                this.selectedSections = []
                this.selectedGroup = []
                this.courseTitle = ''
                this.baseYear = ''
                this.archive = false
                this.createdBy = ''

                this.openCourseForm = false
                this.iseditingCourse = false
                this.editingCourse = false
                this.courseToEdit = null
                this.editStatus = ''

                this.getCourses(this.$store.getters['AppData/cid'])
              })
          }, 1000)
        } else {
          this.feedback = 'There was an error. '

          if (!this.courseTitle)
            this.feedback += 'You are missing a course title. '
          if (!this.courseToEdit)
            this.feedback += 'There was no course to edit. '
        }
      },
      removeUndefinedFields(obj) {
        return Object.keys(obj).reduce((acc, key) => {
          if (obj[key] !== undefined) {
            acc[key] = obj[key]
          }
          return acc
        }, {})
      },
      filterlist(letter, i) {
        this.currPage = 1
        this.activeindex = i
        this.filtered = letter.toLowerCase()
      },
      groupSelected(u) {
        this.filteredGroups.filter((item) => {
          if (item.group_doc_id !== u.group_doc_id)
            item.disabled = !item.disabled

          if (item.hasCourse && !item.checked) item.disabled = true
        })
      },
      resetPagination() {
        this.currPage = 1
      },
      loadCourseToEdit(course) {
        // console.log(course)
        this.feedback = ''
        // check to see if this course is already being edited and if so close the edit panel
        if (
          this.editingCourse &&
          this.courseToEdit &&
          this.courseToEdit.title == course.title
        ) {
          this.openSectionForm = false
          this.editingCourse = false
        } else {
          this.editingCourse = true
          this.courseToEdit = course
          this.selectedUnits = []
          this.selectedSections = []
          this.editStatus = course.status
          this.startDate = course.startDate
          this.selectedGroup = []
          this.baseYear = course.baseYear
          this.archive = course.archive
          this.createdBy = course.createdBy
          this.theMission = course.mission
          this.requiredLanguage = course.requiredLanguage || 'en' // NEW: Load required language
          this.filterLanguage = ''
          this.activeLanguageIndex = undefined
          if (course.base) this.base = course.base

          // Reset the groups array
          this.groups.forEach((x) => {
            x.checked = false
            x.disabled = false
          })

          // preselect the group for this course
          if (course.selectedGroup && course.selectedGroup.length > 0) {
            for (let index = 0; index < course.selectedGroup.length; index++) {
              // get the ID of the group
              const groupID = course.selectedGroup[index].id

              // get the group for this course
              const isCourseGroup = this.groups.find(
                (s) => s.group_doc_id == groupID
              )

              // if we find a group
              if (isCourseGroup) {
                // we have to set a small delay
                setTimeout(() => {
                  isCourseGroup.checked = true
                  this.selectedGroup.push(isCourseGroup)

                  // make sure all other groups are disabled
                  this.filteredGroups.filter((item) => {
                    if (item.group_doc_id !== groupID)
                      item.disabled = !item.disabled
                  })
                }, 100)
              }
            }
          } else {
            // we do not have any preselected groups for this course so make sure all assigned groups are disabled
            this.groups.filter((item) => {
              const allSelectedGroups = this.allGroups.find(
                (s) => s.id == item.group_doc_id
              )

              if (allSelectedGroups) {
                item.hasCourse = true
                item.disabled = true
              }
            })
          }

          if (course.sections && course.sections.length > 0) {
            // loop through and find all the sections of the course and preselect them
            for (let index = 0; index < course.sections.length; index++) {
              // get the ID of the section
              const sectionID = course.sections[index].id

              // look for sections in this course
              const isCourseSection = this.sections.find(
                (s) => s.id == sectionID
              )

              // is the section locked
              // const isSectionLocked = course.sections.find(s => s.id == sectionID).locked
              // const isSectionSuperLocked = course.sections.find(s => s.id == sectionID).superLocked
              const isSectionLocked = course.sections[index].locked
              const isSectionSuperLocked = course.sections[index].superLocked

              // if we find sections
              if (isCourseSection) {
                // we have to set a small delay
                setTimeout(() => {
                  isCourseSection.checked = true
                  isCourseSection.locked = isSectionLocked
                  isCourseSection.superLocked = isSectionSuperLocked

                  this.selectedSections.push(isCourseSection)
                  // make sure the available sections do not include the already selected sections
                  // Filter by language if requiredLanguage is set
                  const filteredSections = this.requiredLanguage
                    ? this.availableSections
                    : this.sections
                  this.list = filteredSections.filter(
                    (item) =>
                      !this.selectedSections.some((d) => d.id === item.id)
                  )
                }, 300)
              }
            }
          }

          // make sure all the available sections are unlocked
          setTimeout(() => {
            this.list.map(function (item) {
              item.locked = false
              item.superLocked = false
              return item
            })
            // listevent only runs when picking a year preset; edit flow builds list here instead
            this.readyToRenderSections = true
          }, 500)

          // console.log('1 ', availableSections)
          // console.log('2 ', course)
          // console.log(this.sectionCategory)
          this.openCourseForm = true
          this.courseTitle = course.title

          this.scrollToTop()
        }
      },
      lockSection(section, i) {
        // only admins can toggle
        if (!this.checkAuthorization(['superAdmin', 'admin'])) return

        // make sure the reactive keys exist
        if (!('locked' in section)) section.locked = false
        if (!('superLocked' in section)) section.superLocked = false

        // toggle
        const updated = {
          ...section,
          locked: !section.locked,
          superLocked: !section.superLocked,
        }

        // replace the item to ensure reactivity
        this.selectedSections.splice(i, 1, updated)
      },
      lookUpGroup(groupID) {
        // console.log('1 ', groupID)
        // console.log('2 ', this.groups)
        const groupName = this.groups.filter(
          (item) => item.group_doc_id === groupID
        )
        if (groupName.length > 0) return groupName[0].name

        return false
      },
      onMove({ relatedContext, draggedContext }) {
        const relatedElement = relatedContext.element
        const draggedElement = draggedContext.element
        // console.log('1', relatedElement, draggedElement)
        return (
          (!relatedElement || !relatedElement.superLocked) &&
          !draggedElement.superLocked
        )
      },
      previewSectionInfo(item) {
        const sectionsUnits = item.units.map((unitID) => {
          return this.allSectionUnits.find((el) => el.id == unitID.id)
        })
        item.units = sectionsUnits
        this.previewSectionInfoMeta = item
        this.showSectionInfo = true
      },
      handlerPopup() {
        this.showSectionInfo = false
      },
      isDraggable(context) {
        // console.log(context)
        const { index, futureIndex } = context
        return !(
          this.selectedSections[index].locked ||
          this.selectedSections[index].superLocked ||
          this.selectedSections[futureIndex].locked ||
          this.selectedSections[futureIndex].superLocked
        )
      },
      scrollToTop() {
        document
          .getElementById('courses')
          .scrollIntoView({ behavior: 'smooth' })
      },
      sortSections(dir) {
        if (dir == 'ASC') {
          this.list.sort((a, b) =>
            a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1
          )
        }
        if (dir == 'DESC') {
          this.list.sort((a, b) =>
            a.title.toLowerCase() < b.title.toLowerCase() ? 1 : -1
          )
        }
      },
      matchesSelectedLanguageFilter(item) {
        if (!(this.editingCourse && this.base && this.filterLanguage)) {
          return true
        }

        const sectionLang = item.requiredLanguage || 'en'
        return sectionLang === this.filterLanguage
      },
      matchesAvailableFilters(item) {
        let ok = true
        if (this.whichYear)
          ok = ok && this.checkForDefaultYear(item).includes(this.whichYear)
        if (this.numOfWeeks) ok = ok && item.units.length === this.numOfWeeks
        if (this.topics)
          ok = ok && (item.category || []).some((c) => c.id == this.topics)
        if (this.authors) ok = ok && item.authorName === this.authors
        if (this.filterLanguage) {
          const sectionLang = item.requiredLanguage || 'en'
          ok = ok && sectionLang === this.filterLanguage
        }
        return ok
      },

      // Same as above but WITHOUT the weeks clause
      matchesAvailableFiltersExceptWeeks(item) {
        let ok = true
        if (this.whichYear)
          ok = ok && this.checkForDefaultYear(item).includes(this.whichYear)
        if (this.topics)
          ok = ok && (item.category || []).some((c) => c.id == this.topics)
        if (this.authors) ok = ok && item.authorName === this.authors
        if (this.filterLanguage) {
          const sectionLang = item.requiredLanguage || 'en'
          ok = ok && sectionLang === this.filterLanguage
        }
        return ok
      },
      init() {
        this.isloading = true
        this.openCourseForm = false
        this.editingCourse = false

        const getUser = this.$store.getters['UserData/getUser']

        // we set a timeout for the User experience
        setTimeout(() => {
          this.sections = []
          let sectionsRef = db.collection('sections')
          sectionsRef
            .get()
            .then((snapshot) => {
              snapshot.forEach((doc) => {
                let section = doc.data()
                section.id = doc.id

                // if the section is set to public and verified or the user CID matches the section CID
                if (
                  this.checkAuthorization(['superAdmin']) ||
                  (section.type == 'public' && section.status == 'verified') ||
                  (section.cid && getUser && section.cid == getUser.cid)
                )
                  this.sections.push(section)

                this.list = this.sections
              })
            })
            .then(() => {
              this.isloading = false
              // Update list when sections are refreshed
              if (this.requiredLanguage) {
                this.list = this.availableSections.filter(
                  (s) => !this.selectedSections.some((sel) => sel.id === s.id)
                )
              } else {
                this.list = this.sections.filter(
                  (s) => !this.selectedSections.some((sel) => sel.id === s.id)
                )
              }
              // console.log("Final Sections:", JSON.stringify(this.list, null, 2));
            })
        }, 1000)
      },
      getCourses(cid) {
        this.courses = []
        this.groups = []
        this.allGroups = []

        let coursesRef = db.collection('courses')
        coursesRef = coursesRef.where('cid', '==', cid)
        coursesRef
          .get()
          .then((snapshot) => {
            snapshot.forEach((doc) => {
              let course = doc.data()
              course.id = doc.id

              if (course.archive) course.disabled = true

              this.courses.push(course)

              this.courses.sort((a, b) => {
                if (a.archive < b.archive) return -1
                if (a.base) return -1
                return 0
              })
            })
          })
          .then(() => {
            // grab all the groups
            for (let i = 0; i < this.courses.length; i++) {
              const groups = this.courses[i].selectedGroup
              if (groups) {
                for (let m = 0; m < groups.length; m++) {
                  this.allGroups.push(groups[m])
                }
              }
            }

            let groupsRef = db.collection('groups')
            groupsRef = groupsRef.where('cid', '==', cid)
            groupsRef
              .get()
              .then((snapshot) => {
                snapshot.forEach((doc) => {
                  let group = doc.data()
                  group.group_doc_id = doc.id
                  this.groups.push(group)
                })
              })
              .then(() => {
                this.init()
              })

            if (this.checkAuthorization(['superAdmin'])) {
              // used to grab all courses that need approval
              // this.supsCourses()

              let baseCoursesRef = db.collection('courses')
              baseCoursesRef = baseCoursesRef.where('base', '==', true)
              baseCoursesRef.get().then((snapshot) => {
                snapshot.forEach((doc) => {
                  let baseCourse = doc.data()
                  baseCourse.id = doc.id

                  this.courses.push(baseCourse)

                  this.courses.sort((a, b) => {
                    if (a.archive < b.archive) return -1
                    if (a.base) return -1
                    return 0
                  })
                })
              })
            }

            this.allSectionUnits = []
            let unitsRef = db.collection('units')
            unitsRef.get().then((snapshot) => {
              snapshot.forEach((doc) => {
                let unit = doc.data()
                unit.id = doc.id
                this.allSectionUnits.push(unit)
              })
            })

            this.allSectionCategories = []
            let catRef = db.collection('categories')
            catRef
              .get()
              .then((snapshot) => {
                snapshot.forEach((doc) => {
                  let cat = doc.data()
                  cat.id = doc.id
                  this.allSectionCategories.push(cat)
                })
              })
              .then(() => {
                this.allSectionCategories.sort((a, b) => {
                  if (a.name < b.name) return -1
                  return 0
                })
              })

            this.getMissions = []
            let missionRef = db.collection('missions')
            missionRef.get().then((snapshot) => {
              snapshot.forEach((doc) => {
                let m = doc.data()
                m.id = doc.id
                this.getMissions.push(m)
              })
            })
            // .then(() => {
            //     console.log('getMissions ', this.getMissions)
            // })

            // console.log('1 ', this.allSectionCategories)
          })
      },
      /* This is used to grab all courses that need approval if the user is the Super admin.
       *  It is called above in the if superAdmin statement
       */
      // async supsCourses() {

      //     let baseCourse = []

      //     let baseCoursesRef = db.collection('courses')

      //     // check if the user is a member or leader
      //     const isBase = baseCoursesRef.where('base', '==', true).get()
      //     const needsApproval = baseCoursesRef.where('status', '==', 'unverified').get()

      //     // wait for the results
      //     const [isBaseQuerySnapshot, needsApprovalQuerySnapshot] = await Promise.all([
      //         isBase,
      //         needsApproval
      //     ])

      //     // get the docs from the results
      //     const isBaseArray = isBaseQuerySnapshot.docs
      //     const needsApprovalArray = needsApprovalQuerySnapshot.docs

      //     // merge docs arrays into one
      //     const superArray = isBaseArray.concat(needsApprovalArray)

      //     // get the user data
      //     superArray.forEach(doc => {
      //         baseCourse = doc.data()
      //         baseCourse.id = doc.id

      //         this.courses.push(baseCourse)

      //         this.courses.sort((a, b) => {
      //             if(a.archive < b.archive) return -1
      //             if(a.base) return -1
      //             return 0
      //         })

      //     })
      // }
    },
    created() {
      this.getCourses(this.$store.getters['AppData/cid'])

      this.$store.watch(
        () => {
          return this.$store.getters['AppData/getNotificationFeedback'] // could also put a Getter here
        },
        (newValue) => {
          // console.log('newValue 1 ', newValue)
          //something changed do something
          if (newValue) {
            // console.log('newValue 2 ', newValue)
            // if(newValue.code && (newValue.code == 'notification-sent' || newValue.code == 'no-verfied email')) {
            //     this.verifyEmail = true
            // }
            this.feedback = newValue.message
          }
        },
        //Optional Deep if you need it
        {
          deep: true,
        }
      )
    },
  }
</script>

<style>
  #courses .sort-btn span {
    line-height: inherit;
  }
  #courses .v-theme--light .v-btn.v-btn--disabled {
    color: rgba(0, 0, 0, 1) !important;
  }
  #courses .ghost {
    opacity: 0.5;
    background: #c8ebfb;
  }
  #courses .list-group > span {
    min-height: 800px;
    max-height: 800px;
    display: block;
  }
  #courses .list-group {
    list-style: none;
    padding: 10px;
    border-radius: 3px;
    min-height: 800px;
    max-height: 800px;
    border: thin solid rgba(var(--v-theme-on-surface), 0.12);
    overflow: scroll;
    scrollbar-width: none;
  }
  #courses .selected-sections-list {
    background: #f3f9e9;
  }
  #courses .v-theme--dark .selected-sections-list {
    background: #757772;
  }
  .v-theme-light #courses .all-sections:not(.ghost) {
    background: #f8f8f8;
  }
  #courses .handle {
    cursor: grab;
  }
  .all-sections .v-list-item__content,
  .selected-sections .v-list-item__content {
    flex: 1 1 auto;
  }
  .all-sections .v-list-item__icon.section-drag-icon,
  .selected .v-list-item__icon.section-drag-icon {
    margin-right: 20px;
  }
  .all-sections .v-list-item__content.section-title,
  .selected .v-list-item__content.section-title {
    flex: 1 0 60%;
  }
  .all-sections .v-list-item__content.section-units,
  .selected .v-list-item__content.section-units {
    flex: 0 0 auto;
  }
  .all-sections .v-list-item__content.section-title .v-list-item__title,
  .selected .v-list-item__content.section-title .v-list-item__title {
    overflow: auto;
    text-overflow: unset;
    white-space: normal;
    line-height: 1.3;
  }
  .all-sections
    .v-list-item__content.section-title
    .v-list-item__title::-webkit-scrollbar,
  .selected
    .v-list-item__content.section-title
    .v-list-item__title::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }
  .v-theme-light .select-sections .badge {
    margin-left: auto;
    color: rgba(56, 57, 53, 0.6);
  }
  .blueyear.v-card--link:focus:before,
  .greenyear.v-card--link:focus:before,
  .redyear.v-card--link:focus:before {
    opacity: 0;
  }
  .default-years .v-list-item {
    min-height: inherit;
  }
  #courses .archive-icon {
    transform: rotate(-90deg);
  }
  #courses .archive-icon:focus {
    outline: none;
  }
  .all-sections .baseyear-indicator {
    flex: 0 0 10px;
  }
  .baseyear-indicators-container {
    list-style: none;
    display: flex;
  }
  .baseyear-indicators-container li {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 5px;
  }
  .baseyear-indicators-container li:last-child {
    margin-right: 10px;
  }
  span.inBlue,
  span.inGreen,
  span.inRed {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }
  span.inBlue,
  .baseyear-indicators-container li.inBlue,
  .years-filter li.blue-year {
    background: #0373b6;
  }
  span.inGreen,
  .baseyear-indicators-container li.inGreen,
  .years-filter li.green-year {
    background: #048159;
  }
  span.inRed,
  .baseyear-indicators-container li.inRed,
  .years-filter li.red-year {
    background: #6e273d;
  }
  .featured-sections-card-footer .information-icon {
    cursor: pointer;
    color: rgb(var(--v-theme-on-surface)); /* adapts to light/dark */
  }

  .featured-sections-card-footer .information-icon:hover {
    color: #a5d867; /* or use another theme-aware value here if needed */
  }

  .featured-section-title.title {
    line-height: 1.3em;
  }
  .featured-section-category {
    color: #888987;
  }
  .filters-column {
    max-height: 870px;
    overflow: scroll;
    scrollbar-width: none;
  }
  .filters-column::-webkit-scrollbar,
  #courses .list-group::-webkit-scrollbar {
    width: 0 !important;
  }
  .available-sections-col {
    position: relative;
  }
  .available-sections-col:before {
    content: '';
    background: rgba(0, 0, 0, 0.12);
    height: 100%;
    width: 1px;
    position: absolute;
    top: 0;
    left: -20px;
  }
  .filter-expansion-panels.v-expansion-panels .v-expansion-panel {
    border: none;
    background: transparent;
  }
  .filter-expansion-panels .v-expansion-panel .v-label {
    font-weight: 400;
    font-size: 16px;
    text-transform: none;
  }
  .filter-expansion-panels .v-expansion-panel .v-item--active .v-label {
    font-weight: 600;
  }
  ul.years-filter {
    display: flex;
    padding: 0;
    list-style: none;
    justify-content: start;
    margin: 0;
  }
  .years-filter li {
    width: 45px;
    height: 45px;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-content: center;
    justify-content: center;
    font-size: 10px;
    color: #fff;
    font-weight: 900;
    text-align: center;
    margin-right: 10px;
    cursor: pointer;
  }
  .years-filter li:last-child {
    margin-right: 0;
  }
  .years-filter li.blue-year {
    background: #002b45;
  }
  .years-filter li.green-year {
    background: #024731;
  }
  .years-filter li.red-year {
    background: #6e273d;
  }
  .filter-expansion-panels
    .v-expansion-panel--active
    > .v-expansion-panel-header {
    min-height: initial;
  }
  .filter-expansion-panels .v-expansion-panel-content__wrap {
    padding: 0;
  }
  .v-expansion-panel-title {
    min-height: 0 !important;
    padding-top: 4px !important;
    padding-bottom: 4px !important;
    line-height: 1.2;
    font-size: 0.875rem;
  }
  .v-expansion-panel-title__overlay {
    display: none !important; /* optional, depending on your needs */
  }
  .v-expansion-panel-title__icon {
    align-self: center;
  }
  .clear-filter-years.active i {
    color: #000;
  }
  .clear-filter-years,
  .clear-filter-weeks,
  .clear-filter-topics {
    cursor: pointer;
    min-height: 27px;
  }
  ul.topic-filter,
  ul.author-filter {
    padding: 0;
    list-style: none;
    max-height: 350px;
    overflow: scroll;
  }
  ul.topic-filter li,
  ul.author-filter li {
    cursor: pointer;
  }
  .groups-selection-scroll .v-checkbox .v-selection-control {
    min-height: 0px; /* tighten checkbox spacing */
  }
  #courses .v-chip:hover::before {
    opacity: 0;
  }
  .course-message {
    text-align: center;
  }
  #courses .unverified {
    cursor: pointer;
  }
  #courses .verified.v-chip--disabled {
    opacity: 0.6;
  }
  .v-pagination .v-pagination__item--is-active .v-btn__overlay {
    opacity: 1 !important; /* force full visibility */
    background-color: #a3d55d !important; /* vivid green overlay */
  }
  .v-pagination .v-pagination__item--is-active .v-btn__content {
    color: white !important;
    font-weight: bold;
    z-index: 1;
  }
  .search-container {
    min-width: 250px;
    max-width: 300px;
    flex-shrink: 0;
  }

  .search-field .v-field__input {
    padding-left: 10px;
  }

  .search-field .v-label {
    font-weight: 500;
  }

  .search-field input {
    font-size: 16px;
  }
  /* kill Vuetify’s 40px min-width on the lock cell only */
  .lock-item {
    flex: 0 0 auto;
    padding: 0;
  }
  .lock-item .v-list-item__content {
    min-width: 0 !important;
    width: auto;
  }
  .flex-1 {
    flex: 1 1 auto;
    min-width: 0; /* important so long titles truncate instead of pushing */
  }
</style>
